import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  useReactFlow,
  ReactFlowProvider,
  Handle,
  Position,
  MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Select from "react-select";
import { toast } from "sonner";
import { components } from "react-select";
import styles from "./WorkFlow.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPen,
  faPlus,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  ACTION_OPTIONS,
  BODY_VARIABLE_OPTIONS,
  COMPANY_PROPERTY_CONFIG,
  CONTACT_PROPERTY_CONFIG,
  DEAL_PROPERTY_CONFIG,
  EXPECTED_CLOSURE_DATE,
  INVOICE_PROPERTY_CONFIG,
  LEAD_PROPERTY_CONFIG,
  LEAD_STAGE_OPTIONS,
  MODULE_OPTIONS,
  NUMBER_OPERATORS,
  OWNER_OPERATORS,
  PRODUCT_OPERATORS,
  PROJECT_STATUS_OPTIONS,
  // PROJECT_PROPERTY_CONFIG,
  PURCHASE_PROPERTY_CONFIG,
  QUOTATION_PROPERTY_CONFIG,
  QUOTATION_STATUS_OPTIONS,
  RECURRING_INVOICE_PROPERTY_CONFIG,
  SALES_PROPERTY_CONFIG,
  TEXT_OPERATORS,
  TOTAL_BUDGET_OPERATOR,
  TRIGGER_LABEL_MAP,
  // TASK_PROPERTY_CONFIG,
  TRIGGER_OPTIONS_BY_MODULE,
} from "./WorkFlowData";
import {
  getAllCities,
  getAllStates,
  getCountries,
} from "../../Utils/hooks/location";
import { GetRequest, PostRequest } from "../../Utils/hooks/ApiHandler";
import useLocalStorageState from "use-local-storage-state";
import { useNavigate, useParams } from "react-router-dom";
import TagsInput from "react-tagsinput";
import { Button, Col, Row } from "react-bootstrap";
import {imageURL } from "../../Utils/constants/constant";
import { BsArrowLeft } from "react-icons/bs";
import { NotificationTemplates } from './../settings/Preferences/NotificationTemplates';
import { UpgradePlan } from "../../CommonComponents/UpgradePlan";
import { AuthorizationCard } from "../../CommonComponents/AuthorizationCard";
/** FIELD CONFIG PER MODULE */
const getDelayOptions = (delayType) => {
  if (!delayType) return [];
  switch (delayType.value) {
    case "day":
      return [
        { value: "2", label: "2 days" },
        { value: "4", label: "4 days" },
        { value: "6", label: "6 days" },
      ];

    case "hour":
      return [
        { value: "2", label: "2 hours" },
        { value: "4", label: "4 hours" },
        { value: "6", label: "6 hours" },
      ];

    case "month":
      return [
        { value: "2", label: "2 months" },
        { value: "4", label: "4 months" },
        { value: "6", label: "6 months" },
      ];
    default:
      return [];
  }
};
const PROPERTY_CONFIG_BY_MODULE = {
  lead: LEAD_PROPERTY_CONFIG,
  deal: DEAL_PROPERTY_CONFIG,
  contact: CONTACT_PROPERTY_CONFIG,
  company: COMPANY_PROPERTY_CONFIG,
  quotation: QUOTATION_PROPERTY_CONFIG,
  // Project: PROJECT_PROPERTY_CONFIG,
  // Task: TASK_PROPERTY_CONFIG,
  sales: SALES_PROPERTY_CONFIG,
  invoice: INVOICE_PROPERTY_CONFIG,
  recurringInvoice: RECURRING_INVOICE_PROPERTY_CONFIG,
  purchase: PURCHASE_PROPERTY_CONFIG,
};
/* ---------- ZOD SCHEMA ---------- */
const selectOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});
const scheduleSchema = z.object({
  repeatBy: z.string().optional(),
});
const conditionSchema = z.object({
  groupId: z.number().optional(),
  property: selectOptionSchema.nullable(),
  operator: selectOptionSchema.nullable(),
  value: z.any().optional(),
  valueFrom: z.any().optional(),
  valueTo: z.any().optional(),
});
const actionSchema = z
  .object({
    groupId: z.number().optional(),
    type: selectOptionSchema.nullable(),
    delayType: selectOptionSchema,
    delayValue: selectOptionSchema.nullable().optional(),
    config: z.record(z.any()).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.type) return;
    // Send Email config validation
    if (
      data.type?.value === "sendEmail" &&
      data.config &&
      Object.keys(data.config).length > 0
    ) {
      // Validate "to" array
      if (!Array.isArray(data.config.to) || data.config.to.length === 0) {
        ctx.addIssue({
          path: ["config", "to"],
          code: z.ZodIssueCode.custom,
          message: "At least one recipient is required",
        });
      }
      // Validate subject
      if (!data.config.subject || data.config.subject.trim() === "") {
        ctx.addIssue({
          path: ["config", "subject"],
          code: z.ZodIssueCode.custom,
          message: "Subject is required",
        });
      }
      // Validate body
      if (!data.config.body || data.config.body.trim() === "") {
        ctx.addIssue({
          path: ["config", "body"],
          code: z.ZodIssueCode.custom,
          message: "Body is required",
        });
      }
    }
    // WhatsApp validation
    if (data.type.value === "sendWhatsapp") {
      if (!data.config?.templateId) {
        ctx.addIssue({
          path: ["config", "templateId"],
          message: "Template ID is required",
          code: z.ZodIssueCode.custom,
        });
      }
      if (!data.config?.variableValues) {
        ctx.addIssue({
          path: ["config", "variableValues"],
          message: "Template variables required",
          code: z.ZodIssueCode.custom,
        });
      }
    }
    // Update Field validation
    if (data.type.value === "updateField") {
      if (!data.config?.field || !data.config?.value) {
        ctx.addIssue({
          path: ["config"],
          code: z.ZodIssueCode.custom,
          message: "Update field config required",
        });
      }
    }
  });
const workflowSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    description: z.string().min(1, "Description is required"),
    module: selectOptionSchema.nullable(),
    trigger: selectOptionSchema.nullable(),
    schedule: scheduleSchema,
    conditions: z.array(conditionSchema).optional(),
    actions: z.array(actionSchema).optional(),
  })
  .superRefine((data, ctx) => {
    // module & trigger
    if (!data.module) {
      ctx.addIssue({
        path: ["module"],
        message: "Module is required",
        code: z.ZodIssueCode.custom,
      });
    }
    if (!data.trigger) {
      ctx.addIssue({
        path: ["trigger"],
        message: "Trigger is required",
        code: z.ZodIssueCode.custom,
      });
    }
    //  CONDITIONS RULE
    const validConditions =
      data.conditions?.filter((c) => c.property && c.operator) || [];
    if (validConditions.length === 0) {
      ctx.addIssue({
        path: ["conditions"],
        message: "At least one condition is required",
        code: z.ZodIssueCode.custom,
      });
    }
    const validActions = data.actions?.filter((a) => a.type) || [];
    if (validActions.length === 0) {
      ctx.addIssue({
        path: ["actions"],
        message: "At least one action is required",
        code: z.ZodIssueCode.custom,
      });
    }
  });
/* ---------- SELECT STYLES ---------- */
const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 13,
    padding: "0 4px",
    boxShadow: "none",
    border: state.isFocused ? "1px solid #2684FF" : "1px solid #d1d5db",
    "&:hover": {
      borderColor: "#60a5fa",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 4px",
  }),
  input: (base) => ({
    ...base,
    padding: "2px 0",
    margin: 0,
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: 13,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 13,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 34,
    padding: "0 3px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  option: (base) => ({
    ...base,
    fontSize: 13,
    padding: "6px 10px",
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 200,
    overflowY: "auto",
    scrollBehavior: "smooth",
    "::-webkit-scrollbar": {
      width: "8px",
    },
    "::-webkit-scrollbar-track": {
      background: "#f1f1f1",
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-thumb": {
      background: "#c9c9c9",
      borderRadius: "4px",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: "#888",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#3b82f6",
    color: "white",
    fontSize: 12,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "white",
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "white",
    ":hover": {
      backgroundColor: "#ef4444",
      color: "white",
    },
  }),
};
/* ---------- HELPERS ---------- */
const getPropertyConfig = (moduleValue, value) => {
  if (!moduleValue) return undefined;
  const list = PROPERTY_CONFIG_BY_MODULE[moduleValue] || [];
  return list.find((p) => p.value === value);
};
const getOperatorsForPropertyType = (type) => {
  switch (type) {
    case "text":
      return TEXT_OPERATORS;
    case "ownerSelect":
    case "leadSource":
    case "leadStage":
    case "quotationStatus":
    case "projectStatus":
      return OWNER_OPERATORS;
    case "number":
      return NUMBER_OPERATORS; 
    case "productService":
      return PRODUCT_OPERATORS;
    case "date":
      return OWNER_OPERATORS;
    case "expected_closure_date":
      return EXPECTED_CLOSURE_DATE;
    case "totalBudget":
      return TOTAL_BUDGET_OPERATOR;
    default:
      return TEXT_OPERATORS;
  }
};
const getTriggerSummary = (module, trigger) => {
  if (!module) return "Click to select trigger";
  if (!trigger) return `${module.label} · Select event`;
  return `${module.label} · ${trigger.label}`;
};
const getConditionSummaryLines = (conditions) => {
  const valid = (conditions || []).filter((c) => c.property && c.operator);
  if (!valid.length) return ["No condition defined"];
  return valid.map((c, idx) => {
    const p = c.property.label;
    const o = c.operator.label;
    const opVal = c.operator.value;
    let v = "";
    if (opVal === "isEmpty" || opVal === "isNotEmpty") {
      v = "";
    } else if (opVal === "between") {
      const from = c.valueFrom ?? "";
      const to = c.valueTo ?? "";
      v = from || to ? `${from} – ${to}` : "";
    } else if (Array.isArray(c.value)) {
      v = c.value
        .map((item) =>
          typeof item === "object"
            ? item.label || item.value || ""
            : String(item),
        )
        .join(", ");
    } else if (
      typeof c.value === "object" &&
      c.value !== null &&
      "label" in c.value
    ) {
      v = c.value.label;
    } else {
      v = c.value ?? "";
    }
    return `${idx + 1}. ${p} ${o}${v ? ` ${v}` : ""}`;
  });
};
const getActionSummaryLines = (actions) => {
  const valid = (actions || []).filter((a) => a.type);
  if (!valid.length) return ["No action defined"];
  return valid.map((a, idx) => `${idx + 1}. ${a.type.label}`);
};
/* ============================================================
   REACT FLOW CUSTOM NODES
   ============================================================ */
const NODE_W = 240;
const NODE_H = 95;
const TRIGGER_W = 320;
const TRIGGER_H = 85;
const PLUS_SIZE = 30;
const X_GAP = 80;
const Y_GAP = 80;
const CENTER_X = 500; // horizontal offset so all nodes stay in positive X space

// Estimate the rendered height of a condition/action card based on number of lines.
// We use conservative (larger) estimates so the plus button always clears the card bottom.
// Header area: ~42px, each line: ~22px (accounts for word-wrap), padding: ~28px
const estimateCardHeight = (lines = []) => {
  const headerH = 42;
  const paddingH = 28;
  // Each line might wrap — assume avg 1.4 visual lines per logical line
  const lineH = 22;
  const linesH = Math.max(lines.length, 1) * lineH * 1.4;
  return Math.max(NODE_H, Math.ceil(headerH + linesH + paddingH));
};

/* ---- Trigger Node ---- */
const TriggerNode = ({ data }) => {
  const { triggerSummary, configured, activeStep, setActiveStep } = data;
  const isActive = activeStep === "trigger";
  return (
    <div
      onClick={() => setActiveStep("trigger")}
      style={{
        width: TRIGGER_W,
        borderRadius: 10,
        border: `2px solid ${isActive ? "#2563eb" : "#93c5fd"}`,
        background: "linear-gradient(135deg,#eff6ff 0%,#dbeafe 100%)",
        cursor: "pointer",
        boxShadow: isActive
          ? "0 0 0 4px rgba(37,99,235,0.13),0 4px 16px rgba(37,99,235,0.12)"
          : "0 2px 10px rgba(0,0,0,0.07)",
        transition: "all 0.2s",
      }}
    >
      <Handle
        type="source"
        position={Position.Bottom}
        id="s"
        style={{
          background: "#2563eb",
          width: 10,
          height: 10,
          bottom: -5,
          cursor: "crosshair",
        }}
      />
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          padding: "13px 16px",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#bfdbfe",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            boxShadow: "0 2px 8px rgba(37,99,235,0.2)",
          }}
        >
          <i
            className="bi bi-send"
            style={{ fontSize: 16, color: "#1d4ed8" }}
          />
        </div>
        <div>
          <div
            style={{
              display: "inline-block",
              padding: "2px 9px",
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#1d4ed8",
              background: "#dbeafe",
              marginBottom: 5,
              border: "1px solid #bfdbfe",
            }}
          >
            TRIGGER
          </div>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "#1e3a5f",
              lineHeight: 1.35,
            }}
          >
            {triggerSummary} action will be performed
          </div>
          {!configured.trigger && (
            <div
              style={{
                fontSize: 10.5,
                color: "#93c5fd",
                marginTop: 3,
                fontWeight: 500,
              }}
            >
              Click to set trigger
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---- Plus Button Node ---- */
const PlusNode = ({ data }) => {
  const { onClick } = data;
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick && onClick();
      }}
      title="Add step"
      style={{
        width: PLUS_SIZE,
        height: PLUS_SIZE,
        minWidth: PLUS_SIZE,
        minHeight: PLUS_SIZE,
        maxWidth: PLUS_SIZE,
        maxHeight: PLUS_SIZE,
        borderRadius: "50%",
        border: "2px dashed #94a3b8",
        background: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        fontSize: 18,
        lineHeight: 1,
        color: "#64748b",
        fontWeight: 400,
        transition: "all 0.18s",
        boxShadow: "0 1px 6px rgba(0,0,0,0.09)",
        userSelect: "none",
        flexShrink: 0,
        overflow: "hidden",
        position: "relative",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderStyle = "solid";
        e.currentTarget.style.borderColor = "#22c55e";
        e.currentTarget.style.background = "#f0fdf4";
        e.currentTarget.style.color = "#16a34a";
        e.currentTarget.style.transform = "scale(1.15)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderStyle = "dashed";
        e.currentTarget.style.borderColor = "#94a3b8";
        e.currentTarget.style.background = "#fff";
        e.currentTarget.style.color = "#64748b";
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        style={{ opacity: 0, pointerEvents: "none", top: -4 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="s"
        style={{ opacity: 0, pointerEvents: "none", bottom: -4 }}
      />
      <span style={{ lineHeight: 1, display: "block", marginTop: -1 }}>+</span>
    </div>
  );
};

/* ---- Condition Node ---- */
const ConditionNode = ({ data }) => {
  const {
    lines,
    groupId,
    configured,
    activeStep,
    editConditionsNode,
    removeConditionGroup,
    addConditionGroup,
    setActiveStep,
  } = data;
  const isActive = activeStep === "condition";
  return (
    <div
      style={{
        width: NODE_W,
        minHeight: NODE_H,
        borderRadius: 10,
        border: `2px solid ${isActive ? "#d97706" : "#fbbf24"}`,
        background: "linear-gradient(135deg,#fffbeb 0%,#fef3c7 100%)",
        cursor: "pointer",
        boxShadow: isActive
          ? "0 0 0 4px rgba(217,119,6,0.15),0 4px 16px rgba(217,119,6,0.1)"
          : "0 2px 10px rgba(0,0,0,0.07)",
        transition: "all 0.2s",
        position: "relative",
      }}
      onClick={() => {
        editConditionsNode();
      }}
    >
      {/* Top handle — clicking it adds a new condition group */}
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        style={{
          background: "#f59e0b",
          width: 12,
          height: 12,
          top: -6,
          cursor: "crosshair",
          border: "2px solid #fff",
          zIndex: 10,
        }}
        onClick={(e) => {
          e.stopPropagation();
          addConditionGroup();
          setActiveStep("condition");
        }}
      />
      {/* Bottom handle — clicking it adds a new condition group */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="s"
        style={{
          background: "#f59e0b",
          width: 12,
          height: 12,
          bottom: -6,
          cursor: "crosshair",
          border: "2px solid #fff",
          zIndex: 10,
        }}
        onClick={(e) => {
          e.stopPropagation();
          addConditionGroup();
          setActiveStep("condition");
        }}
      />
      <div style={{ padding: "11px 13px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <div
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#92400e",
              background: "#fef9c3",
              border: "1px solid #fde68a",
            }}
          >
            CONDITION
          </div>
          {/* Inline action buttons */}
          <div style={{ display: "flex", gap: 4 }}>
            <button
              type="button"
              style={{
                border: "1px solid #fde68a",
                borderRadius: 5,
                padding: "2px 7px",
                background: "#fef9c3",
                cursor: "pointer",
                fontSize: 11,
                color: "#92400e",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                editConditionsNode();
              }}
              title="Edit condition"
            >
              <FontAwesomeIcon icon={faPen} style={{ fontSize: 10 }} />
            </button>
            <button
              type="button"
              style={{
                border: "1px solid #fecaca",
                borderRadius: 5,
                padding: "2px 7px",
                background: "#fee2e2",
                cursor: "pointer",
                fontSize: 11,
                color: "#b91c1c",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                removeConditionGroup(groupId);
              }}
              title="Remove condition"
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: 10 }} />
            </button>
          </div>
        </div>
        {lines.length === 1 ? (
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "#78350f",
              lineHeight: 1.4,
            }}
          >
            {lines[0]}
          </div>
        ) : (
          <ul
            style={{
              margin: 0,
              paddingLeft: 15,
              fontSize: 11.5,
              color: "#92400e",
            }}
          >
            {lines.map((l, i) => (
              <li key={i} style={{ marginBottom: 2, lineHeight: 1.35 }}>
                {l}
              </li>
            ))}
          </ul>
        )}
        {!configured.condition && (
          <div
            style={{
              fontSize: 10.5,
              color: "#fbbf24",
              marginTop: 4,
              fontWeight: 500,
            }}
          >
            Add &amp; apply conditions
          </div>
        )}
      </div>
    </div>
  );
};

/* ---- Action Node ---- */
const ActionNode = ({ data }) => {
  const {
    lines,
    groupId,
    configured,
    activeStep,
    editActionNode,
    removeActionGroup,
    addActionGroup,
    setActiveStep,
  } = data;
  const isActive = activeStep === "action";
  return (
    <div
      style={{
        width: NODE_W,
        minHeight: NODE_H,
        borderRadius: 10,
        border: `2px solid ${isActive ? "#16a34a" : "#4ade80"}`,
        background: "linear-gradient(135deg,#f0fdf4 0%,#dcfce7 100%)",
        cursor: "pointer",
        boxShadow: isActive
          ? "0 0 0 4px rgba(22,163,74,0.15),0 4px 16px rgba(22,163,74,0.1)"
          : "0 2px 10px rgba(0,0,0,0.07)",
        transition: "all 0.2s",
        position: "relative",
      }}
      onClick={() => {
        editActionNode();
      }}
    >
      {/* Top handle */}
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        style={{
          background: "#22c55e",
          width: 12,
          height: 12,
          top: -6,
          cursor: "crosshair",
          border: "2px solid #fff",
          zIndex: 10,
        }}
        onClick={(e) => {
          e.stopPropagation();
          addActionGroup();
          setActiveStep("action");
        }}
      />
      {/* Bottom handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="s"
        style={{
          background: "#22c55e",
          width: 12,
          height: 12,
          bottom: -6,
          cursor: "crosshair",
          border: "2px solid #fff",
          zIndex: 10,
        }}
        onClick={(e) => {
          e.stopPropagation();
          addActionGroup();
          setActiveStep("action");
        }}
      />
      <div style={{ padding: "11px 13px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 6,
          }}
        >
          <div
            style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#14532d",
              background: "#d1fae5",
              border: "1px solid #a7f3d0",
            }}
          >
            ACTION
          </div>
          {/* Inline action buttons */}
          <div style={{ display: "flex", gap: 4 }}>
            <button
              type="button"
              style={{
                border: "1px solid #a7f3d0",
                borderRadius: 5,
                padding: "2px 7px",
                background: "#d1fae5",
                cursor: "pointer",
                fontSize: 11,
                color: "#14532d",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                editActionNode();
              }}
              title="Edit action"
            >
              <FontAwesomeIcon icon={faPen} style={{ fontSize: 10 }} />
            </button>
            <button
              type="button"
              style={{
                border: "1px solid #fecaca",
                borderRadius: 5,
                padding: "2px 7px",
                background: "#fee2e2",
                cursor: "pointer",
                fontSize: 11,
                color: "#b91c1c",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                removeActionGroup(groupId);
              }}
              title="Remove action"
            >
              <FontAwesomeIcon icon={faTrash} style={{ fontSize: 10 }} />
            </button>
          </div>
        </div>
        {lines.length === 1 ? (
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "#14532d",
              lineHeight: 1.4,
            }}
          >
            {lines[0]}
          </div>
        ) : (
          <ul
            style={{
              margin: 0,
              paddingLeft: 15,
              fontSize: 11.5,
              color: "#166534",
            }}
          >
            {lines.map((l, i) => (
              <li key={i} style={{ marginBottom: 2, lineHeight: 1.35 }}>
                {l}
              </li>
            ))}
          </ul>
        )}
        {!configured.action && (
          <div
            style={{
              fontSize: 10.5,
              color: "#4ade80",
              marginTop: 4,
              fontWeight: 500,
            }}
          >
            Configure &amp; apply actions
          </div>
        )}
      </div>
    </div>
  );
};

/* ---- End Node ---- */
const EndNode = () => (
  <div
    style={{
      width: 52,
      height: 52,
      borderRadius: "50%",
      background: "linear-gradient(135deg,#10b981,#059669)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: "0.06em",
      boxShadow: "0 4px 16px rgba(16,185,129,0.45)",
      border: "2px solid #fff",
    }}
  >
    <Handle
      type="target"
      position={Position.Top}
      id="t"
      style={{
        background: "#10b981",
        width: 8,
        height: 8,
        border: "2px solid #fff",
        top: -4,
      }}
    />
    END
  </div>
);

const NODE_TYPES = {
  triggerNode: TriggerNode,
  conditionNode: ConditionNode,
  actionNode: ActionNode,
  plusNode: PlusNode,
  endNode: EndNode,
};

/* ============================================================
   REACT FLOW CANVAS WRAPPER
   ============================================================ */
const EDGE_STYLE = { stroke: "#94a3b8", strokeWidth: 2 };

const buildFlowGraph = ({
  configured,
  activeStep,
  triggerSummary,
  conditionSummariesByGroup,
  actionSummariesByGroup,
  setActiveStep,
  addConditionGroup,
  removeConditionGroup,
  addActionGroup,
  removeActionGroup,
  editConditionsNode,
  editActionNode,
}) => {
  const nodes = [];
  const edges = [];
  let yOffset = 20;

  const mkEdge = (
    id,
    source,
    target,
    sourceHandle = "s",
    targetHandle = "t",
  ) => ({
    id,
    source,
    target,
    sourceHandle,
    targetHandle,
    type: "smoothstep",
    style: EDGE_STYLE,
    animated: false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      color: "#94a3b8",
      width: 18,
      height: 18,
    },
  });

  const sharedData = (extra) => ({
    configured,
    activeStep,
    setActiveStep,
    editConditionsNode,
    editActionNode,
    removeConditionGroup,
    removeActionGroup,
    addConditionGroup,
    addActionGroup,
    ...extra,
  });

  /* ---- TRIGGER ---- */
  const triggerId = "trigger";
  nodes.push({
    id: triggerId,
    type: "triggerNode",
    position: { x: CENTER_X - TRIGGER_W / 2, y: yOffset },
    data: sharedData({ triggerSummary }),
    draggable: true,
  });
  yOffset += TRIGGER_H + 50;

  if (!configured.trigger) {
    return { nodes, edges };
  }

  /* ---- PLUS (trigger → conditions) ---- */
  const plusTriggerId = "plus-trigger";
  nodes.push({
    id: plusTriggerId,
    type: "plusNode",
    position: { x: CENTER_X - PLUS_SIZE / 2, y: yOffset },
    data: sharedData({
      onClick: () =>
        conditionSummariesByGroup.length > 0
          ? setActiveStep("condition")
          : addConditionGroup(),
    }),
    draggable: false,
  });
  edges.push(mkEdge("e-trig-plusT", triggerId, plusTriggerId));
  yOffset += PLUS_SIZE + 50;

  /* ---- CONDITION GROUPS ---- */
  if (conditionSummariesByGroup.length > 0) {
    const MAX_PER_ROW = 4;
    const n = conditionSummariesByGroup.length;
    const rows = [];
    for (let i = 0; i < n; i += MAX_PER_ROW) {
      rows.push(conditionSummariesByGroup.slice(i, i + MAX_PER_ROW));
    }

    let condRowY = yOffset;
    rows.forEach((rowItems, rowIdx) => {
      const rowCount = rowItems.length;
      const totalW = rowCount * NODE_W + (rowCount - 1) * X_GAP;
      const startX = CENTER_X - totalW / 2;

      // Find the tallest card in this row for consistent plus placement
      const rowMaxH = rowItems.reduce(
        (max, gs) => Math.max(max, estimateCardHeight(gs.lines)),
        NODE_H,
      );

      rowItems.forEach((gs, ci) => {
        const cx = startX + ci * (NODE_W + X_GAP);
        const condId = `cond-${gs.groupId}`;
        nodes.push({
          id: condId,
          type: "conditionNode",
          position: { x: cx, y: condRowY },
          data: sharedData({ lines: gs.lines, groupId: gs.groupId }),
          draggable: true,
        });
        edges.push(mkEdge(`e-plusT-${condId}`, plusTriggerId, condId));

        /* Plus below each condition — use rowMaxH so all plusses align */
        const condPlusId = `plus-cond-${gs.groupId}`;
        nodes.push({
          id: condPlusId,
          type: "plusNode",
          position: {
            x: cx + NODE_W / 2 - PLUS_SIZE / 2,
            y: condRowY + rowMaxH + 55,
          },
          data: sharedData({
            onClick: () => {
              addConditionGroup();
              setActiveStep("condition");
            },
          }),
          draggable: false,
        });
        edges.push(mkEdge(`e-${condId}-${condPlusId}`, condId, condPlusId));
      });

      condRowY += rowMaxH + PLUS_SIZE + Y_GAP + 55;
    });

    yOffset = condRowY;
  }

  /* ---- ACTION SECTION ---- */
  if (configured.condition) {
    /* Hub plus that collects all condition branches → action fan-out */
    const actHubId = "plus-action-hub";
    nodes.push({
      id: actHubId,
      type: "plusNode",
      position: { x: CENTER_X - PLUS_SIZE / 2, y: yOffset },
      data: sharedData({
        onClick: () => {
          addActionGroup();
          setActiveStep("action");
        },
      }),
      draggable: false,
    });
    /* Connect every condition-plus to hub */
    if (conditionSummariesByGroup.length > 0) {
      conditionSummariesByGroup.forEach((gs) => {
        edges.push(
          mkEdge(
            `e-condPlus-${gs.groupId}-hub`,
            `plus-cond-${gs.groupId}`,
            actHubId,
          ),
        );
      });
    } else {
      edges.push(mkEdge("e-plusT-actHub", plusTriggerId, actHubId));
    }
    yOffset += PLUS_SIZE + 50;

    if (actionSummariesByGroup.length > 0) {
      const MAX_PER_ROW = 4;
      const n = actionSummariesByGroup.length;
      const rows = [];
      for (let i = 0; i < n; i += MAX_PER_ROW) {
        rows.push(actionSummariesByGroup.slice(i, i + MAX_PER_ROW));
      }

      let actRowY = yOffset;
      rows.forEach((rowItems, rowIdx) => {
        const rowCount = rowItems.length;
        const totalW = rowCount * NODE_W + (rowCount - 1) * X_GAP;
        const startX = CENTER_X - totalW / 2;

        // Find the tallest card in this row
        const rowMaxH = rowItems.reduce(
          (max, gs) => Math.max(max, estimateCardHeight(gs.lines)),
          NODE_H,
        );

        rowItems.forEach((gs, ai) => {
          const ax = startX + ai * (NODE_W + X_GAP);
          const actId = `act-${gs.groupId}`;
          nodes.push({
            id: actId,
            type: "actionNode",
            position: { x: ax, y: actRowY },
            data: sharedData({ lines: gs.lines, groupId: gs.groupId }),
            draggable: true,
          });
          edges.push(mkEdge(`e-hub-${actId}`, actHubId, actId));

          const actPlusId = `plus-act-${gs.groupId}`;
          nodes.push({
            id: actPlusId,
            type: "plusNode",
            position: {
              x: ax + NODE_W / 2 - PLUS_SIZE / 2,
              y: actRowY + rowMaxH + 55,
            },
            data: sharedData({
              onClick: () => {
                addActionGroup();
                setActiveStep("action");
              },
            }),
            draggable: false,
          });
          edges.push(mkEdge(`e-${actId}-${actPlusId}`, actId, actPlusId));
        });

        actRowY += rowMaxH + PLUS_SIZE + Y_GAP + 55;
      });

      yOffset = actRowY;
    }
  } // end if (configured.condition)

  /* ---- END NODE ---- */
  if (configured.trigger) {
    const endId = "end";
    nodes.push({
      id: endId,
      type: "endNode",
      position: { x: CENTER_X - 26, y: yOffset },
      data: {},
      draggable: false,
    });

    /* Connect last plus nodes → END */
    const actPlusNodes = nodes.filter((n) => n.id.startsWith("plus-act-"));
    const actHubNode = nodes.find((n) => n.id === "plus-action-hub");
    const condPlusNodes = nodes.filter((n) => n.id.startsWith("plus-cond-"));

    if (actPlusNodes.length > 0) {
      actPlusNodes.forEach((pn) =>
        edges.push(mkEdge(`e-${pn.id}-end`, pn.id, endId)),
      );
    } else if (actHubNode && actionSummariesByGroup.length === 0) {
      edges.push(mkEdge("e-actHub-end", actHubNode.id, endId));
    } else if (condPlusNodes.length > 0 && !configured.condition) {
      condPlusNodes.forEach((pn) =>
        edges.push(mkEdge(`e-${pn.id}-end`, pn.id, endId)),
      );
    } else {
      /* fallback: last plus → end */
      const lastPlus = [...nodes].reverse().find((n) => n.type === "plusNode");
      if (lastPlus)
        edges.push(mkEdge(`e-${lastPlus.id}-end-fb`, lastPlus.id, endId));
    }
  }

  return { nodes, edges };
};

/* Suppress benign ResizeObserver loop error from ReactFlow (CRA overlay fix) */
if (typeof window !== 'undefined') {
  window.addEventListener(
    'error',
    function (e) {
      if (e && e.message && String(e.message).indexOf('ResizeObserver loop') !== -1) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    },
    true,
  );
}




const WorkflowCanvasInner = (props) => {
  const { isEditMode } = props;
  const { fitView, setViewport } = useReactFlow();

  /* Single memo — rebuilt when structure OR activeStep changes.
     Previously the dual-memo pattern (one for structure, one for activeStep)
     caused a race where setEdges from the structure memo ran but was then
     overwritten by the activeStep memo's stale edge list, losing connections. */
  const { nodes: layoutNodes, edges: layoutEdges } = useMemo(
    () => buildFlowGraph(props),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.configured,
      props.triggerSummary,
      props.conditionSummariesByGroup,
      props.actionSummariesByGroup,
      props.conditionGroups,
      props.actionGroups,
      props.activeStep,
    ],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  /* Edit mode mein graph invisible rakhna jab tak fitView settle na ho */
  const [visible, setVisible] = React.useState(!isEditMode);
  const didFirstFit = React.useRef(false);

  /* Viewport ref — saves current pan/zoom in memory so it survives graph updates */
  const savedViewport = React.useRef(null);

  /* Track whether we've already laid out once (to detect activeStep-only changes) */
  const lastStructureKey = React.useRef(null);

  /* When graph changes: set nodes/edges and fitView */
  useEffect(() => {
    if (!layoutNodes.length) return;

    // Build a structure key (excludes activeStep) to detect if layout changed or just highlight
    const structureKey = layoutNodes.map((n) => n.id).join(",");
    const isStructureChange = structureKey !== lastStructureKey.current;
    lastStructureKey.current = structureKey;

    if (isStructureChange) {
      // Full structure change — set everything
      setNodes(layoutNodes);
      setEdges(layoutEdges);

      if (savedViewport.current) {
        const vp = savedViewport.current;
        setTimeout(() => setViewport({ x: vp.x, y: vp.y, zoom: vp.zoom }, { duration: 0 }), 30);
      } else if (!didFirstFit.current) {
        didFirstFit.current = true;
        if (isEditMode) {
          setTimeout(() => {
            setEdges([...layoutEdges]);
            fitView({ padding: 0.25, maxZoom: 1.0, duration: 0 });
            setTimeout(() => setVisible(true), 40);
          }, 60);
        } else {
          setTimeout(() => fitView({ padding: 0.25, maxZoom: 1.0, duration: 200 }), 100);
        }
      } else if (isEditMode) {
        setTimeout(() => setEdges([...layoutEdges]), 20);
      }
    } else {
      // Only activeStep changed — update node data but preserve positions & edges
      setNodes((current) =>
        current.map((n) => {
          const updated = layoutNodes.find((u) => u.id === n.id);
          return updated ? { ...n, data: updated.data } : n;
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutNodes, layoutEdges]);

  /* Save viewport on every pan/zoom move */
  const handleMove = useCallback((_event, viewport) => {
    savedViewport.current = viewport;
  }, []);

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 330px)",
        minHeight: 500,
        opacity: visible ? 1 : 0,
        transition: isEditMode ? "opacity 0.25s ease" : "none",
      }}
    >
      <style>{`
        .react-flow__attribution { display: none !important; }
        .react-flow__handle { transition: transform 0.15s, box-shadow 0.15s; }
        .react-flow__handle:hover { transform: scale(1.4); box-shadow: 0 0 0 4px rgba(37,99,235,0.25); }
        .react-flow__node { transition: filter 0.15s; }
        .react-flow__node:hover { filter: brightness(0.97); }
        .react-flow__edge-path { stroke-width: 2; }
        .react-flow__controls { box-shadow: 0 2px 12px rgba(0,0,0,0.1); border-radius: 10px; overflow: hidden; }
        .react-flow__controls-button { border: none !important; border-bottom: 1px solid #f1f5f9 !important; }
        .react-flow__minimap { border-radius: 10px; border: 1px solid #e2e8f0; overflow: hidden; }
      `}</style>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onMove={handleMove}
        nodeTypes={NODE_TYPES}
        defaultEdgeOptions={{
          type: "smoothstep",
          style: EDGE_STYLE,
          animated: false,
        }}
        panOnDrag
        panOnScroll={false}
        zoomOnScroll
        zoomOnPinch
        minZoom={0.25}
        maxZoom={2.5}
        proOptions={{ hideAttribution: true }}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
      >
        <Background color="#dde1e7" gap={22} size={1.2} variant="dots" />
        <Controls showInteractive={false} style={{ bottom: 16, left: 16 }} />
        <MiniMap
          nodeColor={(n) => {
            if (n.type === "triggerNode") return "#93c5fd";
            if (n.type === "conditionNode") return "#fbbf24";
            if (n.type === "actionNode") return "#4ade80";
            if (n.type === "endNode") return "#10b981";
            return "#e2e8f0";
          }}
          style={{ background: "#f8fafc", bottom: 16, right: 16 }}
          maskColor="rgba(248,250,252,0.7)"
        />
      </ReactFlow>
    </div>
  );
};

const WorkflowCanvas = (props) => (
  <ReactFlowProvider>
    <WorkflowCanvasInner {...props} />
  </ReactFlowProvider>
);

/* ---------- COMPONENT ---------- */

const WorkFlow = () => {
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  });

  const customer_id = customer?.customer_details?.customer_id || 1;
  const token = customer?.token?.authorization || "";
  const rf = customer?.token?.authentication || "";
  const headers = {
    Authorization: token,
    Authentication: rf,
  };
  const navigate = useNavigate();

  const { paramId } = useParams();

  const permission = customer.customer_details?.permission;

  // permissions to handle
  const isWorkFlow = Boolean(
    permission?.find((c) => c.module_name === "workflow"),
  );

  const workFlowPermission =
    isWorkFlow && permission?.filter((c) => c.module_name === "workflow");
  const user_type = Number(customer.customer_details?.user_type);
  const workflowGrants = {
    isView:
      user_type !== 1
        ? Boolean(
            Number(
              isWorkFlow &&
                workFlowPermission?.find((c) => c.feature_name === "workflow")
                  ?.view,
            ),
          )
        : true,
    isCreate:
      user_type !== 1
        ? Boolean(
            Number(
              isWorkFlow &&
                workFlowPermission?.find((c) => c.feature_name === "workflow")
                  ?.create,
            ),
          )
        : true,
    isEdit:
      user_type !== 1
        ? Boolean(
            Number(
              isWorkFlow &&
                workFlowPermission?.find((c) => c.feature_name === "workflow")
                  ?.edit,
            ),
          )
        : true,
    isDelete:
      user_type !== 1
        ? Boolean(
            Number(
              isWorkFlow &&
                workFlowPermission?.find((c) => c.feature_name === "workflow")
                  ?.delete,
            ),
          )
        : true,
    isRevised:
      user_type !== 1
        ? Boolean(
            Number(
              isWorkFlow &&
                workFlowPermission?.find((c) => c.feature_name === "workflow")
                  ?.is_action,
            ),
          )
        : true,
  };



  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(workflowSchema),
    defaultValues: {
      name: "",
      description: "",
      module: null,
      trigger: null,
      schedule: {
        repeatBy: "",
      },
      conditions: [],
      actions: [],
    },
  });

  const selectedModule = watch("module");
  const selectedTrigger = watch("trigger");
  const conditions = watch("conditions") || [];
  const actions = watch("actions") || [];

  const {
    fields: conditionFields,
    append: appendCondition,
    remove: removeCondition,
  } = useFieldArray({
    control,
    name: "conditions",
  });

  const {
    fields: actionFields,
    append: appendAction,
    remove: removeAction,
  } = useFieldArray({
    control,
    name: "actions",
  });

  const [selectedCountry, setSelectedCountry] = useState({
    value: 99,
    label: "India",
  });
  const [country, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [city, setCity] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conditionalFetch, setConditionalFetch] = useState();
  const [emailTemplate, setEmailTemplate] = useState([]);
  const [tags, setTags] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState([]);
  const [pipelineOption, setpipelineOption] = useState([]);
  const [LEAD_SOURCE_OPTIONS, setLeadSource] = useState([]);
  const [show, setShow] = useState(false);
  const [moduleType, setModuleType] = useState(false);

  useEffect(() => {
    // jab edit data load ho

    if (isEditMode) {
      setConfigured({
        trigger: true,
        condition: true,
        action: true,
      });
      setActiveStep("action");
    } else {
      setActiveStep("trigger");
    }

    // default open ACTION tab
  }, [isEditMode]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getCountries();

        if (!data) return;
        // Update state once with all country data
        const countryOptions = data.map((country) => ({
          value: country.id,
          label: country.name,
        }));
        setCountries(countryOptions);
        // Find India in the options and set it as selected
        const indiaOption = countryOptions.find(
          (c) => c.value === 99 || c.label === "India",
        );
        if (indiaOption) {
          setSelectedCountry(indiaOption);
          setValue("countryId", indiaOption.value.toString());
        }
      } catch (error) {
        return;
      }
    })();
  }, [setValue]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllStates();

        if (!data) return;
        // Update state once with all country data
        const state_option = data.map((state) => ({
          value: state.id,
          label: state.state_name,
        }));
        setStates(state_option);
      } catch (error) {
        return;
      }
    })();
  }, [setValue]);
  useEffect(() => {
    (async () => {
      try {
        const data = await getAllCities();

        if (!data) return;
        // Update state once with all country data
        const city_option = data.map((city) => ({
          value: city.id,
          label: city.city_name,
        }));
        setCity(city_option);
      } catch (error) {
        return;
      }
    })();
  }, [setValue]);
  const [dynamicFields, setDynamicFields] = useState([]);
  const [whatsappTemplateDataOption, setWhatsappTemplateDataOption] = useState(
    [],
  );
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [LEAD_OWNER_OPTIONS, setLeadOwner] = useState([]);
  const [productService, setProductService] = useState([]);
  const [addteamplateX, setAddTemplate] = useState(false);
  const handleAddTemplateClick = () => {
    setAddTemplate(true);
    setShow(true);
    setModuleType("WhatsApp");
  };

  const fetchLeadSource = async () => {
    const res = await GetRequest(
      `masters/getAllLead_source/${customer_id}`,
      { headers },
      { customer, setCustomer, rf: headers.Authentication, navigate },
    );
    if (!res) return;
    if (res.success === false) {
      return;
    }

    setLeadSource(
      res.data.map((lead) => ({
        label: lead.source_title,
        value: lead.id,
      })),
    );
  };

  useEffect(() => {
    fetchLeadSource();
  }, []);

  useEffect(() => {
    if (selectedModule?.value === "lead" || selectedModule?.label === "Deal") {
      (async () => {
        const res = await GetRequest(
          `/lead/fetchPipelines/${customer.customer_details.customer_id}/${selectedModule?.value}s/${customer?.customer_details?.id}/${customer?.customer_details?.user_type}`,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate },
        );

        if (res.err) {
          return;
        }
        const owners =
          res.data &&
          res.data.map((lead) => ({
            label: lead.pipeline_name,
            value: lead.id,
          }));
        setpipelineOption(owners);
      })();
    } else {
      return;
    }
  }, [selectedModule]);

  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `users/users/fetchUserAsOwner/${customer.customer_details.customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate },
      );
      if (res.err) {
        console.error(res.err);
      }
      const owners =
        res.data &&
        res.data.map((lead) => ({
          label: lead.full_name,
          value: lead.id,
        }));
      setLeadOwner(owners);
    })();
  }, []);
  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `/masters/getProducts/${customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate },
      );
      if (res.err) {
        toast.error("Error Fetching Data");
      }

      const product =
        res.data &&
        res.data.map((pro) => ({
          label: pro.service,
          value: pro.id,
        }));
      setProductService(product);
    })();
  }, []);

  const renderDynamicFields = () => {
    return dynamicFields.map((field) => {
      const value = configModal.draft.variableValues?.[field.name] || null;

      if (field.name.startsWith("header_")) {
        if (field.type === "image") {
          return (
            <div key={field.name} className="col-md-12">
              <label className="form-label">
                {field.label} <span className="text-danger">*</span>
              </label>

              {/* IMAGE PREVIEW  */}
              {configModal.draft.headerImagePreview && (
                <div className="mb-2">
                  <img
                    src={`${configModal.draft.headerImagePreview}`}
                    alt="Header Preview"
                    style={{
                      maxWidth: "100%",
                      height: "120px",
                      objectFit: "contain",
                      border: "1px solid #ddd",
                    }}
                  />
                  <small className="text-muted">
                    Existing image (upload new to replace)
                  </small>
                </div>
              )}

              {/*  FILE INPUT */}
              <input
                type="file"
                accept="image/*"
                className="form-control mb-2"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;

                  updateConfigDraft("variableValues", {
                    ...configModal.draft.variableValues,
                    [field.name]: file,
                  });

                  if (file) {
                    updateConfigDraft(
                      "headerImagePreview",
                      URL.createObjectURL(file),
                    );
                  }
                }}
              />
            </div>
          );
        }

        // HEADER TEXT
        return (
          <div key={field.name} className="col-md-12">
            <label className="form-label">
              {field.label} <span className="text-danger">*</span>
            </label>

            <input
              type="text"
              className="form-control mb-2"
              placeholder="Enter header text"
              value={value || ""}
              onChange={(e) =>
                updateConfigDraft("variableValues", {
                  ...configModal.draft.variableValues,
                  [field.name]: e.target.value,
                })
              }
            />
          </div>
        );
      }

      //  BUTTON URL
      if (field.name.startsWith("button_")) {
        return (
          <div key={field.name} className="col-md-12">
            <label className="form-label">
              {field.label} <span className="text-danger">*</span>
            </label>

            <input
              type="url"
              className="form-control mb-2"
              placeholder="Enter button URL"
              value={value || ""}
              onChange={(e) =>
                updateConfigDraft("variableValues", {
                  ...configModal.draft.variableValues,
                  [field.name]: e.target.value,
                })
              }
            />
          </div>
        );
      }

      // {body variable}
      return (
        <div key={field.name} className="col-md-12">
          <label className="form-label">
            {field.label} <span className="text-danger">*</span>
          </label>

          <Select
            options={BODY_VARIABLE_OPTIONS}
            placeholder="Select variable"
            value={
              BODY_VARIABLE_OPTIONS.find((opt) => opt.value === value) || null
            }
            onChange={(selected) =>
              updateConfigDraft("variableValues", {
                ...configModal.draft.variableValues,
                [field.name]: selected?.value || "",
              })
            }
            styles={customSelectStyles}
            isClearable
          />
        </div>
      );
    });
  };

  const onWhatsAPPFetch = async () => {
    const templateId = configModal.draft.templateId;
    // const templatename =config.draft.templateName;

    const templatedName = configModal?.draft?.templateName;

    if (!templateId) {
      toast.error("Enter template ID");
      return;
    }

    setIsLoading(true);

    try {
      //  Always clear old data before fetch
      updateConfigDraft("templateContent", "");
      updateConfigDraft("variableValues", {});
      setDynamicFields([]);

      const res = await GetRequest(
        `/integration/fetchWhatsappTemplateContent/${templatedName}/${customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate },
      );

      if (!res?.data) {
        throw new Error("No template data found");
      }

      //  Safe JSON parsing
      let parsed;
      try {
        parsed = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
      } catch {
        throw new Error("Invalid template response");
      }

      const template = parsed?.data?.[0];
      if (!template) {
        throw new Error("Template not found");
      }

      const language = template?.languages?.[0];
      if (!language) {
        throw new Error("Template language data missing");
      }

      //  BODY content
      const body = Array.isArray(language.code)
        ? language.code.find((c) => c.type === "BODY")
        : null;

      //  Variables
      const variables = Array.isArray(language.variables)
        ? language.variables
        : [];

      const variableTypes = language.variable_type || {};

      // Build dynamic fields
      const fields = variables.map((v) => ({
        name: v,
        type: variableTypes?.[v]?.type || "text",
        label: v.startsWith("header")
          ? "Header"
          : v.startsWith("button")
            ? "Button URL"
            : "Body Variable",
      }));

      setDynamicFields(fields);

      //  Preserve already mapped values (if same template refetched)
      const existingValues = configModal.draft.variableValues || {};
      const mappedValues = {};

      variables.forEach((v) => {
        if (existingValues[v] !== undefined) {
          mappedValues[v] = existingValues[v];
        }
      });

      updateConfigDraft("templateContent", body?.text || "");
      updateConfigDraft("variableValues", mappedValues);
    } catch (error) {
      console.error("WhatsApp template fetch error:", error);
      toast.error(error.message || "Failed to fetch WhatsApp template");

      // RESET on error
      updateConfigDraft("templateContent", "");
      updateConfigDraft("variableValues", {});
      setDynamicFields([]);
    } finally {
      setIsLoading(false);
    }
  };

  const validateWhatsappModal = () => {
    const draft = configModal.draft || {};

    if (!draft.name || !draft.name.trim()) {
      toast.error("Name is required");
      return false;
    }

    if (!Array.isArray(draft.to) || draft.to.length === 0) {
      toast.error("Please select at least one 'To'");
      return false;
    }

    if (!draft.templateContent || !draft.templateContent.trim()) {
      toast.error("Please fetch WhatsApp template");
      return false;
    }

    for (const field of dynamicFields) {
      const value = draft.variableValues?.[field.name];

      // HEADER
      if (field.name.startsWith("header_")) {
        if (field.type === "image") {
          // image: preview OR new file
          if (!draft.headerImagePreview && !(value instanceof File)) {
            toast.error("Header image is required");
            return false;
          }
        } else {
          if (!value || !value.toString().trim()) {
            toast.error("Header text is required");
            return false;
          }
        }
      }

      // BODY
      if (field.name.startsWith("body_")) {
        if (!value || !value.toString().trim()) {
          toast.error("All body variables are required");
          return false;
        }
      }

      // BUTTON
      if (field.name.startsWith("button_")) {
        if (!value || !value.toString().trim()) {
          toast.error("Button URL is required");
          return false;
        }
      }
    }

    return true;
  };
  const validateEmailModal = () => {
    const draft = configModal.draft || {};

    // 1️ Name
    if (!draft.name || !draft.name.trim()) {
      toast.error("Name is required");
      return false;
    }

    // 2️ To (MULTI SELECT)
    if (!Array.isArray(draft.to) || draft.to.length === 0) {
      toast.error("Please select at least one 'To'");
      return false;
    }

    // 3️ Template
    if (!draft.template_id) {
      toast.error("Please select email template");
      return false;
    }

    // 4️ Subject
    if (!draft.subject || !draft.subject.trim()) {
      toast.error("Subject is required");
      return false;
    }

    // 5️ Body
    if (!draft.body || !draft.body.trim()) {
      toast.error("Email body is required");
      return false;
    }

    return true;
  };

  // fetch email tempelate

  const fetchEmailTemplate = async () => {
    try {
      const res = await GetRequest(
        `workflow/fetchEmailTemplate/${customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate },
      );

      if (res.status === "fail") {
        return;
      }

      // ---- Convert into Select dropdown format ----
      const options = res?.data?.map((item) => ({
        value: item.id,
        label: item.template_name,
      }));

      setEmailTemplate(res?.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchEmailTemplate();
  }, [addteamplateX]);

  const normalizeToField = (to) => {
    if (!to) return [];
    if (Array.isArray(to) && Array.isArray(to[0])) {
      return to[0]; // [["owner","manager"]] → ["owner","manager"]
    }
    if (Array.isArray(to)) return to;
    return [];
  };

  const fetchWorkFlowOfSpecificId = async () => {
    try {
      if (!paramId) return;
      if (!workflowGrants?.isEdit) {
        toast.warning("your are not authorized to edit workflow");
        return navigate("/workflow");
      }
      setIsEditMode(true);

      const res = await GetRequest(
        `workflow/fetchSpecificWorkflowDetails/${customer_id}/${paramId}/${customer?.customer_details?.id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate },
      );

      if (res.status !== "success") {
        return;
      }

      const workflow = Array.isArray(res.data) ? res.data[0] : res.data;

      const formattedActions =
        workflow.actions?.map((action, index) => {
          const details = JSON.parse(action.details || "{}");

          if (action.step_type === "whatsapp") {
            const wd = details.whatsapp_details || {};

            const variableValues = {};

            // BODY
            (wd.template_variable_type || []).forEach((key, i) => {
              variableValues[key] = wd.variables_value?.[i] || "";
            });

            // HEADER TEXT
            if (wd.header_text) {
              variableValues["header_1"] = wd.header_text;
            }

            // BUTTON
            if (Array.isArray(wd.variables_value_url)) {
              wd.variables_value_url.forEach((url, i) => {
                variableValues[`button_${i + 1}`] = url;
              });
            }

            // HEADER IMAGE URl
            const headerImagePreview = action.image
              ? `${imageURL}${action.image}`
              : "";

            return {
              groupId: index + 1,
              type: { value: "sendWhatsapp", label: "Send WhatsApp" },
              delayType: {
                value: action.delay,
                label: action.delay.replace("_", " "),
              },
              delayValue: {
                value: String(action.interval || "0"),
                label: `${action.interval || 0} ${action.delay}`,
              },
              config: {
                name: action.step_name || "",
                to: Array.isArray(details.to)
                  ? details.to
                  : details.to
                    ? [details.to]
                    : [],
                recipients: details.recipients || [],
                templateId: wd.template_template_id || "",
                templateName: wd.template_name || "",
                templateContent: wd.template_content || "",
                variableValues,
                headerImagePreview,
              },
            };
          }

          //  EMAIL
          return {
            groupId: index + 1,
            type: { value: "sendEmail", label: "Send Email" },
            delayType: {
              value: action.delay,
              label: action.delay.replace("_", " "),
            },
            delayValue: {
              value: action.interval || 0,
              label: `${action.interval || 0} ${action.delay}`,
            },
            config: {
              name: action.step_name || "",
              to: details.to || [],
              recipients: details.recipients || [],
              template_id: details.template_id || "",
              subject: details.subject || "",
              body: details.body || "",
            },
          };
        }) || [];

      // CONDITIONS
      const rawConditions = workflow.conditions?.[0]?.conditions
        ? JSON.parse(workflow.conditions[0].conditions)
        : [];

      const formattedConditions = rawConditions.map((c, index) => ({
        groupId: Number(c.groupId) || index + 1,
        property: c.property,
        operator: c.operator,
        value: c.value,
        valueFrom: c.valueFrom || "",
        valueTo: c.valueTo || "",
      }));

      const formattedDetails = {
        name: workflow.workflow_name,
        description: workflow.description,
        module: {
          value: workflow.module_name,
          label: workflow.module_name,
        },
        trigger: {
          value: workflow.trigger,
          label: TRIGGER_LABEL_MAP[workflow.trigger] || workflow.trigger,
        },
        schedule: { repeatBy: "" },
        conditions: formattedConditions,
        actions: formattedActions,
      };

      reset(formattedDetails);
      setEditedData(formattedDetails);

      setConfigured({
        trigger: true,
        condition: true,
        action: true,
      });

    } catch (error) {
      console.error("Edit workflow fetch error:", error);
    }
  };

  useEffect(() => {
    if (!paramId) {
      return;
    }
    fetchWorkFlowOfSpecificId();
  }, [paramId]);

  const handleTagChange = (newTags) => {
    const uniqueTags = [];
    newTags.forEach((tag) => {
      const lowerTag = tag.toLowerCase();
      const isDuplicate = uniqueTags.some((t) => t.toLowerCase() === lowerTag);
      if (!isDuplicate) {
        uniqueTags.push(tag);
      }
    });
    setTags(uniqueTags);
  };

  const handleEmailTemplateSelect = (templateId) => {
    if (!templateId) {
      updateConfigDraft("template_id", "");
      updateConfigDraft("template_name", "");
      updateConfigDraft("subject", "");
      updateConfigDraft("body", "");
      return;
    }

    const selectedTemplate = emailTemplate.find((t) => t.id === templateId);

    if (!selectedTemplate) return;

    updateConfigDraft("template_id", selectedTemplate.id);
    updateConfigDraft("template_name", selectedTemplate.template_name);
    updateConfigDraft("subject", selectedTemplate.subject || "");
    updateConfigDraft("body", selectedTemplate.body || "");
  };

  const [activeStep, setActiveStep] = useState("trigger");
  const [conditionType, setConditionType] = useState(null);

  const [configured, setConfigured] = useState({
    trigger: false,
    condition: false,
    action: false,
  });

  // action config modal
  const [configModal, setConfigModal] = useState({
    open: false,
    actionIndex: null,
    actionType: null,
    draft: {},
  });

  const triggerOptionsForModule = useMemo(() => {
    if (!selectedModule) return [];

    return TRIGGER_OPTIONS_BY_MODULE[selectedModule.value] || [];
  }, [selectedModule]);

  const eventTriggerOptions = useMemo(
    () => triggerOptionsForModule.filter((t) => t.value !== "scheduledEvents"),
    [triggerOptionsForModule],
  );

  const propertyOptionsForModule = useMemo(() => {
    if (!selectedModule) return [];
    return PROPERTY_CONFIG_BY_MODULE[selectedModule.value] || [];
  }, [selectedModule]);

  /* ---------- GROUP HELPERS ---------- */

  const conditionGroups = useMemo(() => {
    const map = new Map();
    conditionFields.forEach((field, idx) => {
      const cond = conditions[idx] || {};
      const gid = cond.groupId ?? 1;
      if (!map.has(gid)) map.set(gid, []);
      map.get(gid).push({ field, index: idx, condition: cond });
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([groupId, rows]) => ({ groupId, rows }));
  }, [conditionFields, conditions]);

  const actionGroups = useMemo(() => {
    const map = new Map();
    actionFields.forEach((field, idx) => {
      const act = actions[idx] || {};
      const gid = act.groupId ?? 1;
      if (!map.has(gid)) map.set(gid, []);
      map.get(gid).push({ field, index: idx, action: act });
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([groupId, rows]) => ({ groupId, rows }));
  }, [actionFields, actions]);

  const getNextConditionGroupId = () => {
    const ids = (conditions || [])
      .map((c) => c.groupId)
      .filter((x) => typeof x === "number");
    const max = ids.length ? Math.max(...ids) : 0;
    return max + 1 || 1;
  };

  const getNextActionGroupId = () => {
    const ids = (actions || [])
      .map((a) => a.groupId)
      .filter((x) => typeof x === "number");
    const max = ids.length ? Math.max(...ids) : 0;
    return max + 1 || 1;
  };

  const addConditionGroup = () => {
    const newId = getNextConditionGroupId();
    appendCondition({
      groupId: newId,
      property: null,
      operator: null,
      value: "",
      valueFrom: "",
      valueTo: "",
    });
    setActiveStep("condition");
  };

  const addConditionRowToGroup = (groupId) => {
    appendCondition({
      groupId,
      property: null,
      operator: null,
      value: "",
      valueFrom: "",
      valueTo: "",
    });
  };

  const removeConditionGroup = (groupId) => {
    const indexesToRemove = conditions
      .map((c, idx) => ({ c, idx }))
      .filter(({ c }) => (c.groupId ?? 1) === groupId)
      .map(({ idx }) => idx)
      .sort((a, b) => b - a);

    indexesToRemove.forEach((idx) => removeCondition(idx));
  };

  const addActionGroup = () => {
    const newId = getNextActionGroupId();
    appendAction({
      groupId: newId,
      type: null,
      delayType: "immediate",
      delayValue: "",
      config: {},
    });
    setActiveStep("action");
  };

  const addActionRowToGroup = (groupId) => {
    appendAction({
      groupId,
      type: null,
      delayType: "immediate",
      delayValue: "",
      config: {},
    });
  };

  const removeActionGroup = (groupId) => {
    const indexesToRemove = actions
      .map((a, idx) => ({ a, idx }))
      .filter(({ a }) => (a.groupId ?? 1) === groupId)
      .map(({ idx }) => idx)
      .sort((a, b) => b - a);

    indexesToRemove.forEach((idx) => removeAction(idx));
  };

  /* ---------- ACTION CONFIG MODAL HELPERS ---------- */

  const openConfigModal = (index) => {
    setActiveStep(null);
    const action = actions[index];
    if (!action || !action.type) return;
    setConfigModal({
      open: true,
      actionIndex: index,
      actionType: action.type.value,
      draft: action.config || {},
    });
  };

  const closeConfigModal = () => {
    setActiveStep("action");
    setConfigModal({
      open: false,
      actionIndex: null,
      actionType: null,
      draft: {},
    });
  };

  const updateConfigDraft = (field, value) => {
    setConfigModal((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        [field]: value,
      },
    }));
  };

//  search saveConfigModal
 const saveConfigModal = () => {
    //  WHATSAPP VALIDATION
    if (configModal.actionType === "sendWhatsapp") {
      if (!validateWhatsappModal()) return;
    }
    //  EMAIL VALIDATION
    if (configModal.actionType === "sendEmail") {
      if (!validateEmailModal()) return;
    }
    //  EXISTING LOGIC
    if (configModal.actionIndex != null) {
      setValue(
        `actions.${configModal.actionIndex}.config`,
        configModal.draft || {},
      );
    }
    if (configModal.actionType === "sendWhatsapp") {
    setSelectedTemplate(null);
    updateConfigDraft("templateId", "");
    updateConfigDraft("templateName", "");
    updateConfigDraft("templateContent", "");
    updateConfigDraft("templateVariables", {});
    updateConfigDraft("variableValues", {});
    setDynamicFields([]);
  }
    closeConfigModal();
  };

  /* ---------- VALUE INPUT RENDER ---------- */

  const renderValueInput = (index, propertyConfig, operator) => {
    const base = `conditions.${index}`;
    const opVal = operator?.value;

    //  If operator is "is empty" / "is not empty", hide value input
    if (opVal === "isEmpty" || opVal === "isNotEmpty") {
      return null;
    }

    if (!propertyConfig) {
      return (
        <input
          className="form-control form-control-sm"
          placeholder="Value"
          {...register(`${base}.value`)}
        />
      );
    }

    const type = propertyConfig.type;

    // Lead & Contact owner select
    if (type === "ownerSelect") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={LEAD_OWNER_OPTIONS}
              placeholder="Select s owner"
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }
    if (type === "pipelineSelect") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={pipelineOption}
              placeholder="Select pipeline"
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }
    if (type === "productService") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={productService}
              placeholder="Select Product"
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }

    if (type === "email") {
      return (
        <input
          type="email"
          className="form-control form-control-sm"
          placeholder="Enter email"
          {...register(`${base}.value`, {
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Invalid email format",
            },
          })}
        />
      );
    }
    if (type === "expected_closure_date") {
      if (opVal === "between") {
        return (
          <div className="d-flex gap-1">
            <input
              type="number"
              placeholder="From (days)"
              className="form-control form-control-sm"
              {...register(`${base}.valueFrom`, { required: true })}
            />
            <input
              type="number"
              placeholder="To (days)"
              className="form-control form-control-sm"
              {...register(`${base}.valueTo`, { required: true })}
            />
          </div>
        );
      }

      // After / Before → single number input
      if (opVal === "after" || opVal === "before") {
        return (
          <input
            type="number"
            placeholder="Days"
            className="form-control form-control-sm"
            {...register(`${base}.value`, { required: true })}
          />
        );
      }

      // Default Date Input
      return (
        <input
          type="date"
          className="form-control form-control-sm"
          {...register(`${base}.value`)}
        />
      );
    }

    if (type === "country") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={country}
              placeholder={`Select ${type}`}
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }
    if (type === "state") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={states}
              placeholder={`Select ${type}`}
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }
    if (type === "city") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={city}
              placeholder={`Select ${type}`}
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }

    if (type === "leadStage") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              isMulti
              closeMenuOnSelect={false}
              options={LEAD_STAGE_OPTIONS}
              placeholder="Select stage"
              styles={customSelectStyles}
            />
          )}
        />
      );
    }

    if (type === "leadSource") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={LEAD_SOURCE_OPTIONS}
              placeholder="Select source"
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }

    if (type === "quotationStatus") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={QUOTATION_STATUS_OPTIONS}
              placeholder="Select status"
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }

    if (type === "projectStatus") {
      return (
        <Controller
          name={`${base}.value`}
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={PROJECT_STATUS_OPTIONS}
              placeholder="Select status"
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
      );
    }

    if (type === "number") {
      //  Between → Min & Max inputs
      if (opVal === "between") {
        return (
          <div className="d-flex gap-1">
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Min"
              {...register(`${base}.valueFrom`)}
            />
            <input
              type="number"
              className="form-control form-control-sm"
              placeholder="Max"
              {...register(`${base}.valueTo`)}
            />
          </div>
        );
      }

      return (
        <input
          type="number"
          className="form-control form-control-sm"
          placeholder="Enter value"
          {...register(`${base}.value`)}
        />
      );
    }

    if (type === "date") {
      return (
        <input
          type="date"
          className="form-control form-control-sm"
          {...register(`${base}.value`)}
        />
      );
    }

    // default text input
    return (
      <input
        className="form-control form-control-sm"
        placeholder="Value"
        {...register(`${base}.value`)}
      />
    );
  };

  /* ---------- APPLY STEP HANDLERS ---------- */

  const handleApplyTrigger = () => {
    if (!selectedModule || !selectedTrigger) {
      toast.error("Please select module and event");
      return;
    }
    setConfigured((prev) => ({ ...prev, trigger: true }));

    // ensure first condition group created
    if (!conditions.length) {
      addConditionGroup();
    }

    setActiveStep("condition");
  };

  const handleApplyCondition = () => {
    const hasAnyValid = (conditions || []).some(
      (c) => c?.property && c?.operator,
    );
    if (!hasAnyValid) {
      toast.error("Please add complete condition fields or group");
      return;
    }

    const invalidValue = (conditions || []).some((c) => {
      if (!c?.property || !c?.operator) return false;
      const opVal = c.operator.value;

      if (opVal === "isEmpty" || opVal === "isNotEmpty") return false;

      if (c.property.value === "expected_closure_date") {
        if (opVal === "between") {
          return !c.valueFrom || !c.valueTo;
        }

        if (opVal === "after" || opVal === "before") {
          return !c.value;
        }
      }

      return !c.value;
    });

    if (invalidValue) {
      toast.error("Please fill values for all conditions");
      return;
    }

    setConfigured((prev) => ({ ...prev, condition: true }));
    setActiveStep("action");
  };

  // handle apply action
  // const handleApplyAction = () => {

  //   const hasAnyAction = (actions || []).some((a) => a?.type);
  //   if (!hasAnyAction) {
  //     return toast.error("Please add complete data in  action group ");
  //   }

  //   const invalidConfig = (actions || []).some((a) => {
  //     if (!a.type) return false;

  //     if (a.type.value === "sendEmail") {
  //       return !a.config?.to || !a.config?.subject || !a.config?.body;
  //     }
  //     if (a.type.value === "sendWhatsapp") {
  //       return (
  //         !a.config?.templateId ||
  //         !a.config?.templateContent ||
  //         !a.config?.variableValues ||
  //         Object.keys(a.config.variableValues).length === 0
  //       );
  //     }
  //     if (a.type.value === "updateField") {
  //       return !a.config?.field || !a.config?.value;
  //     }

  //     return false;
  //   });

  //   if (invalidConfig) {
  //     return toast.error("Please complete action configurations");
  //   }

  //   setConfigured((prev) => ({ ...prev, action: true }));
  // };

  const handleApplyAction = () => {
    const hasAnyAction = (actions || []).some((a) => a?.type);
    if (!hasAnyAction) {
      return toast.error("Please add complete data in action group");
    }

    const invalidConfig = (actions || []).some((a) => {
      if (!a.type) return false;

      if (a.type.value === "sendEmail") {
        return !a.config?.to || !a.config?.subject || !a.config?.body;
      }
      if (a.type.value === "sendWhatsapp") {
        return (
          !a.config?.templateId ||
          !a.config?.templateContent ||
          !a.config?.variableValues ||
          Object.keys(a.config.variableValues).length === 0
        );
      }
      if (a.type.value === "updateField") {
        return !a.config?.field || !a.config?.value;
      }

      return false;
    });

    if (invalidConfig) {
      return toast.error("Please complete action configurations");
    }

    setConfigured((prev) => ({ ...prev, action: true }));

    toast.success(
      "All fields and conditions applied successfully. You can save this workflow.",
    );

    // CLOSE RIGHT SIDEBAR DRAWER
    setActiveStep(null);
  };

  /* ---------- EDIT / DELETE (nodes) ---------- */

  const editConditionsNode = () => {
    if (!configured.trigger) {
      toast.error("Set trigger first");
      return;
    }
    setActiveStep("condition");
  };

  const deleteConditionsNode = () => {
    reset({
      name: watch("name"),
      description: watch("description"),
      module: watch("module"),
      trigger: watch("trigger"),
      schedule: { repeatBy: "" },
      conditions: [],
      actions,
    });
    setConfigured((prev) => ({ ...prev, condition: false }));
  };

  const editActionNode = () => {
    if (!configured.condition) {
      toast.error("Complete conditions first");
      return;
    }
    setActiveStep("action");
  };

  const deleteActionNode = () => {
    reset({
      name: watch("name"),
      description: watch("description"),
      module: watch("module"),
      trigger: watch("trigger"),
      schedule: { repeatBy: "" },
      conditions,
      actions: [],
    });
    setConfigured((prev) => ({ ...prev, action: false }));
  };
  //menu list
  const MenuList = (props) => {
    const { isEditMode } = props.selectProps;

    return (
      <components.MenuList {...props}>
        {props.children}

        {!isEditMode && (
          <div
            style={{
              padding: "10px",
              borderTop: "1px solid #ddd",
              textAlign: "center",
              cursor: "pointer",
              color: "#0d6efd",
              fontWeight: 500,
            }}
            onClick={() => props.selectProps.onAddNew()}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Template
          </div>
        )}
      </components.MenuList>
    );
  };
  const handleAddEmailTemplateClick = () => {
    setAddTemplate(true);
    setShow(true);
    setModuleType("Email");
  };

  /* ---------- SUBMIT / CANCEL ---------- */
  const buildPayload = (data) => {
    const finalConditions = (data.conditions || []).filter(
      (c) => c.property && c.operator,
    );

    const headerImageMeta = [];
    const formattedActions = data.actions.map((a, index) => {
      const actionType = a.type?.value;

      let type = "";
      let details = {};

      // EMAIL
      if (actionType === "sendEmail") {
        type = "email";
        details = {
          to: a.config?.to || [],
          recipients: a.config?.recipients || [],
          subject: a.config?.subject || "",
          body: a.config?.body || "",
          template_id: a.config?.template_id || "",
        };
      }

      if (actionType === "sendWhatsapp") {
        type = "whatsapp";
        const variableValues = a.config?.variableValues || {};
        let headerFile = null;
        let headerPreview = null;
        let headerText = "";
        const bodyVariableTypes = [];
        const bodyVariableValues = [];
        const bodyVariableTypeOfType = [];
        const buttonUrls = [];

        Object.entries(variableValues).forEach(([key, value]) => {
          // HEADER
          if (key.startsWith("header_")) {
            if (value instanceof File) {
              headerFile = value;
            } else if (typeof value === "string") {
              headerText = value;
            }
            return;
          }

          // BUTTON
          if (key.startsWith("button_")) {
            buttonUrls.push(value);
            return;
          }

          // BODY
          bodyVariableTypes.push(key);
          bodyVariableValues.push(value);
          bodyVariableTypeOfType.push(value instanceof File ? "image" : "text");
        });

        // existing image (edit mode)
        if (a.config?.headerImagePreview) {
          const preview = a.config.headerImagePreview;
          const uploadIndex = preview.indexOf("/upload/");
          headerPreview =
            uploadIndex !== -1 ? preview.substring(uploadIndex) : preview;
        }

        headerImageMeta[index] = {
          hasFile: !!headerFile, // true if new file selected
          file: headerFile || null, // File (handled via FormData)
          path: headerFile ? null : headerPreview, // existing image path
        };

        details = {
          to: Array.isArray(a.config?.to)
            ? a.config.to
            : a.config?.to
              ? [a.config.to]
              : [],
          recipients: a.config?.recipients || [],

          whatsapp_details: {
            template_name: a.config?.templateName || "",
            template_namespace: a.config?.templateNamespace || "",
            template_language: a.config?.templateLanguage || "en",
            template_template_id: a.config?.templateId || "",

            template_body_count: bodyVariableValues.length.toString(),
            template_variable_count: bodyVariableValues.length.toString(),

            template_variable_type: bodyVariableTypes,
            template_variable_typeOFtype: bodyVariableTypeOfType,

            template_content: a.config?.templateContent || "",

            // BODY
            variables_value: bodyVariableValues,

            // HEADER TEXT ONLY
            header_text: headerText,

            // BUTTONS
            variables_value_url: buttonUrls,
          },
        };
      }

      //  SMS
      if (actionType === "sendSMS") {
        type = "sms";
        details = {
          to: a.config?.to || [],
          recipients: a.config?.recipients || [],
          body: a.config?.body || "",
        };
      }

      // UPDATE FIELD
      if (actionType === "updateField") {
        type = "update_field";
        details = {
          field: a.config?.field,
          value: a.config?.value,
        };
      }

      return {
        step_name: a.config?.name || "",
        type,
        delay: a.delayType?.value || "immediate",
        interval: a.delayValue?.value || 0,
        details,
      };
    });

    return {
      customer_id,
      workflow_name: data.name,
      description: data.description || "",
      module_name: data?.module?.value,
      trigger: data?.trigger?.value,
      user_login_id: customer?.customer_details?.id,
      conditions: finalConditions,
      action: formattedActions,
      header_image: headerImageMeta || "",
    };
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true)
      if (!configured.trigger) {
        toast.error("Please apply trigger");
        setActiveStep("trigger");
        return;
      }
      if (!configured.condition) {
        toast.error("Please apply conditions");
        setActiveStep("condition");
        return;
      }
      if (!configured.action) {
        toast.error("Please apply action");
        setActiveStep("action");
        return;
      }

      const payload = buildPayload(data);
      let res;

      if (isEditMode) {
        //  UPDATE WORKFLOW (PUT)
        res = await PostRequest(
          `/workflow/updateWorkflow`,
          {
            ...payload,

            workflow_id: paramId,
          },
          true,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate },
        );
      } else {
        //  CREATE WORKFLOW (POST)
        res = await PostRequest(
          `/workflow/generateWorkFlow`,
          payload,
          true,
          { headers },
          { customer, setCustomer, rf: headers.Authentication, navigate },
        );
      }

      if (res.status !== "success") {
        return toast.error(res.message || "Error submitting workflow");
      }

      // Migrate layout from "new" key to the real workflow id
      if (!isEditMode) {
        const newWorkflowId = res.data?.id || res.data?.[0]?.id;
        if (newWorkflowId) {
        }
      }

      toast.success(res.message || "Workflow saved successfully!");
      navigate("/workflow");
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false)
    }
  };

  useEffect(() => {
    (async () => {
      const res = await GetRequest(
        `/workflow/fetchWhatsappTemplate/${customer?.customer_details?.customer_id}`,
        { headers },
        { customer, setCustomer, rf: headers.Authentication, navigate },
      );

      if (res?.err) {
        toast.error("Error Fetching Data");
        return;
      }

      const options =
        res?.data?.map((t) => ({
          label: t?.template_name,
          value: t?.id,
        })) || [];

      setWhatsappTemplateDataOption(options);

      if (isEditMode && configModal?.draft?.templateId) {
        const selected = options.find(
          (opt) => opt.value === configModal.draft.templateId,
        );

        if (selected) {
          setSelectedTemplate(selected);
        } else {
          setSelectedTemplate(configModal?.draft?.templateId);
        }
      }
    })();
  }, [addteamplateX, isEditMode, configModal?.draft?.templateId]);

  const handleCancel = () => {
    reset();
    setActiveStep("trigger");
    setConfigured({ trigger: false, condition: false, action: false });
    setConditionType(null);
  };

  /* ---------- SIDEBAR PANELS ---------- */

  useEffect(() => {
    if (isEditMode) {
      if (
        configModal.open &&
        configModal.actionType === "sendWhatsapp" &&
        configModal.draft.templateId
      ) {
        onWhatsAPPFetch();
      }
    }
  }, [configModal.open]);

  useEffect(() => {
    if (configModal.open && configModal.actionType === "sendWhatsapp") {
      updateConfigDraft(
        "headerImagePreview",
        configModal.draft.headerImagePreview || "",
      );
    }
  }, [configModal.open]);

  const renderTriggerSidebar = () => (
    <div className={styles.sidebarSection}>
      {/* Workflow meta box stays on top */}
      <div
        className={styles.workflowMetaBox}
        style={{ zIndex: 5, position: "relative" }}
      >
        <div>
          <h6 className={styles.metaTitle}>Name your Workflow</h6>
          <div className="mb-2">
            <div className="d-flex flex-column mb-1">
              <span
                style={{
                  fontSize: "0.86rem",
                  color: "#878b8e",
                  fontWeight: "400",
                }}
              >
                Workflow Name<span className="text-danger">*</span>
              </span>
              <span className="text-muted fs-10 fw-400 mb-1">
                Enter a unique name to identify what this workflow is used for.
              </span>
            </div>
            <input
              className="form-control form-control-sm"
              style={{ maxWidth: "38.25rem" }}
              {...register("name")}
              placeholder="Enter workflow name"
            />
            {errors.name && (
              <small className="text-danger">{errors.name.message}</small>
            )}
          </div>
          <div className="mb-1">
            <div className="d-flex flex-column mb-1">
              <span
                style={{
                  fontSize: "0.86rem",
                  color: "#878b8e",
                  fontWeight: "400",
                }}
              >
                Description<span className="text-danger">*</span>
              </span>
              <span className="text-muted fs-10 fw-400 mb-1">
                Briefly describe when this workflow runs and what actions it
                performs.
              </span>
            </div>
            <textarea
              className="form-control form-control-sm"
              style={{ maxWidth: "30.25rem" }}
              rows={2}
              {...register("description")}
              placeholder="Description"
            />
            {errors.description && (
              <small className="text-danger">
                {errors.description.message}
              </small>
            )}
          </div>
        </div>
      </div>
      <div className={styles.sidebarHeaderRow}>
        <span className={styles.sidebarStepNumber}>1</span>
        <div>
          <div className={styles.sidebarTitle}>Trigger</div>
          <div className={styles.sidebarSubTitle}>
            Start automation when an event occurs.
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label className={styles.formLabel}>Item</label>
        <Controller
          name="module"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={MODULE_OPTIONS}
              placeholder="Select item"
              isClearable
              styles={customSelectStyles}
            />
          )}
        />
        {errors.module && (
          <small className="text-danger">{errors.module.message}</small>
        )}
      </div>

      <div className="mb-2">
        <label className={styles.formLabel}>Event</label>
        <Controller
          name="trigger"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={eventTriggerOptions}
              placeholder="Select event"
              isClearable
              styles={customSelectStyles}
              isDisabled={!selectedModule}
            />
          )}
        />
        {errors.trigger && (
          <small className="text-danger">{errors.trigger.message}</small>
        )}
      </div>

      <div className={styles.applyBar}>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={handleCancel}
        >
          Reset
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm"
          onClick={handleApplyTrigger}
          disabled={!selectedModule || !selectedTrigger}
        >
          Apply trigger
        </button>
      </div>
    </div>
  );

  const renderConditionSidebar = () => {
    if (!conditionType) {
      // Step selector (Pipedrive style)
      return (
        <div className={styles.sidebarSection}>
          <div className={styles.sidebarHeaderRow}>
            <span className={styles.sidebarStepNumber}>2</span>
            <div>
              <div className={styles.sidebarTitle}>Next step</div>
              <div className={styles.sidebarSubTitle}>
                Choose how you want to filter the records.
              </div>
            </div>
          </div>

          <div className={styles.triggerTypeGrid}>
            <button
              type="button"
              className={styles.stepOptionCard}
              onClick={() => setConditionType("instant")}
            >
              <div className={styles.stepOptionTitle}>Instant condition</div>
              <div className={styles.stepOptionText}>
                Only continue automation if the condition is met.
              </div>
            </button>

            <button
              type="button"
              className={`${styles.stepOptionCard} ${styles.stepOptionDisabled}`}
            >
              <div className={styles.stepOptionTitle}>
                If / else condition
                <span className={styles.badgeNew}>Soon</span>
              </div>
              <div className={styles.stepOptionText}>
                Branch automation when condition is met or not met.
              </div>
            </button>
          </div>

          <div className={styles.applyBar}>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setActiveStep("trigger")}
            >
              Back
            </button>
          </div>
        </div>
      );
    }

    // Instant condition editor with groups
    return (
      <div className={styles.sidebarSection}>
        <div className={styles.sidebarHeaderRow}>
          <span className={styles.sidebarStepNumber}>2</span>
          <div>
            <div className={styles.sidebarTitle}>Instant condition</div>
            <div className={styles.sidebarSubTitle}>
              Define conditions that must be true before the workflow continues.
            </div>
          </div>
        </div>

        {conditionGroups.map((group) => (
          <div key={group.groupId} className={styles.conditionGroupCard}>
            <div className={styles.conditionGroupHeader}>
              <span className={styles.conditionGroupTitle}>
                Condition group {group.groupId}
              </span>
              <button
                type="button"
                style={{
                  border: "none",
                  color: "red",
                  backgroundColor: "#ffff",
                }}
                disabled={conditionGroups.length === 1}
                onClick={() =>
                  conditionGroups.length > 1 &&
                  removeConditionGroup(group.groupId)
                }
              >
                <FontAwesomeIcon icon={faXmark} className="ms-0" />
              </button>
            </div>

            {group.rows.map(({ index }) => {
              const cond = conditions[index] || {};
              const propertyValue = cond?.property?.value;
              const propertyConfig = getPropertyConfig(
                selectedModule?.value,
                propertyValue,
              );
              const operators = getOperatorsForPropertyType(
                propertyConfig?.type,
              );
              const operator = cond?.operator || null;

              return (
                <div key={conditionFields[index].id}>
                  <div className={styles.conditionBox}>
                    <div className={styles.conditionIndex}>{index + 1}.</div>

                    <div className={styles.conditionFields}>
                      {/* Row 1: property + operator */}
                      <div className={styles.conditionRow}>
                        <div className={styles.conditionCol}>
                          <Controller
                            name={`conditions.${index}.property`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={propertyOptionsForModule}
                                placeholder="Choose field"
                                onChange={(selected) => {
                                  field.onChange(selected);
                                  setValue(
                                    `conditions.${index}.operator`,
                                    null,
                                  );
                                  setValue(`conditions.${index}.value`, "");
                                  setValue(`conditions.${index}.valueFrom`, "");
                                  setValue(`conditions.${index}.valueTo`, "");
                                }}
                                styles={customSelectStyles}
                              />
                            )}
                          />
                        </div>

                        <div className={styles.conditionCol}>
                          <Controller
                            name={`conditions.${index}.operator`}
                            control={control}
                            render={({ field }) => (
                              <Select
                                {...field}
                                options={operators}
                                placeholder="Operator"
                                isDisabled={!propertyConfig}
                                styles={customSelectStyles}
                                onChange={(val) => {
                                  field.onChange(val);
                                  // reset values when operator changes
                                  setValue(`conditions.${index}.value`, "");
                                  setValue(`conditions.${index}.valueFrom`, "");
                                  setValue(`conditions.${index}.valueTo`, "");
                                }}
                              />
                            )}
                          />
                        </div>
                      </div>

                      {/* Row 2: value + delete */}
                      <div className={styles.conditionRow}>
                        <div className={styles.conditionValueCol}>
                          {renderValueInput(index, propertyConfig, operator)}
                        </div>

                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          disabled={conditionFields.length === 1}
                          onClick={() =>
                            conditionFields.length > 1 && removeCondition(index)
                          }
                        >
                          −
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <button
              type="button"
              className="btn btn-link btn-sm px-0 mt-1"
              onClick={() => addConditionRowToGroup(group.groupId)}
            >
              <FontAwesomeIcon icon={faPlus} /> Add condition in this group
            </button>
          </div>
        ))}

        <button
          type="button"
          className="btn btn-link btn-sm px-0 mt-1"
          onClick={addConditionGroup}
        >
          <FontAwesomeIcon icon={faPlus} /> Add condition group
        </button>

        <div className={styles.applyBar}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setConditionType(null)}
          >
            Back
          </button>
          <button
            type="button"
            className="btn btn-success btn-sm"
            onClick={handleApplyCondition}
          >
            Apply conditions
          </button>
        </div>
      </div>
    );
  };

  //action side bar

  const renderActionSidebar = () => (
    <div className={styles.sidebarSection}>
      <div className={styles.sidebarHeaderRow}>
        <span className={styles.sidebarStepNumber}>3</span>
        <div>
          <div className={styles.sidebarTitle}>Actions</div>
          <div className={styles.sidebarSubTitle}>
            Define what should happen automatically, step by step.
          </div>
        </div>
      </div>

      <div className={styles.actionRowSidebar}>
        {actionGroups.map((group) => (
          <div key={group.groupId} className={styles.actionGroupCard}>
            <div className={styles.actionGroupHeader}>
              <span className={styles.actionGroupTitle}>
                Action group {group.groupId}
              </span>
              <button
                type="button"
                style={{
                  border: "none",
                  backgroundColor: "#ffff",
                  color: "red",
                }}
                disabled={actionGroups.length === 1}
                onClick={() =>
                  actionGroups.length > 1 && removeActionGroup(group.groupId)
                }
              >
                <FontAwesomeIcon icon={faXmark} className="ms-0" />
              </button>
            </div>

            {group.rows.map(({ index }) => {
              const action = actions[index] || {};

              return (
                <div key={actionFields[index].id} className={styles.actionCard}>
                  <div className={styles.actionCardHeader}>
                    <div className={styles.actionCardTitle}>
                      Action {index + 1}
                    </div>
                    {action.type && (
                      <span className={styles.actionCardBadge}>
                        {action.type.label}
                      </span>
                    )}
                  </div>

                  {/* row: type + delay */}
                  <div className={styles.actionRow}>
                    <div className={styles.actionCol}>
                      <div className={`${styles.smallLabel} mb-1`}>
                        Action type
                      </div>
                      <Controller
                        name={`actions.${index}.type`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            options={ACTION_OPTIONS}
                            placeholder="Choose action"
                            isClearable
                            styles={customSelectStyles}
                          />
                        )}
                      />
                    </div>

                    <div className={styles.actionCol}>
                      <div className={`${styles.smallLabel} mb-1`}>Run</div>
                      <Controller
                        name={`actions.${index}.delayType`}
                        control={control}
                        render={({ field }) => {
                          const options = [
                            { value: "immediate", label: "Immediately" },
                            {
                              value: "hour",
                              label: "After X hours",
                            },
                            { value: "day", label: "After X days" },
                          ];

                          return (
                            <Select
                              options={options}
                              value={field.value ?? null}
                              onChange={(val) => {
                                field.onChange(val);

                                setValue(`actions.${index}.delayValue`, null);
                              }}
                              isClearable={false}
                              styles={customSelectStyles}
                            />
                          );
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    {action.delayType?.value !== "immediate" && (
                      <>
                        <div className="d-flex flex-column">
                          <div className={styles.smallLabel}>Delay</div>

                          <div style={{ width: "13.6rem" }}>
                            <Controller
                              name={`actions.${index}.delayValue`}
                              control={control}
                              render={({ field }) => (
                                <Select
                                  options={getDelayOptions(action.delayType)}
                                  value={field.value ?? null}
                                  onChange={(val) => field.onChange(val)}
                                  styles={customSelectStyles}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </>
                    )}
                    <button
                      type="button"
                      className={`${styles.configButton}  btn btn-outline-secondary btn-sm `}
                      disabled={!action.type}
                      style={{ marginTop: "2rem", height: "36px" }}
                      onClick={() => openConfigModal(index)}
                    >
                      Configure…
                    </button>
                  </div>
                </div>
              );
            })}

            {/* <button
              type="button"
              className="btn btn-link btn-sm px-0 mt-1"
              onClick={() => addActionRowToGroup(group.groupId)}
            >
              + Add action in this group
            </button> */}
          </div>
        ))}

        <button
          type="button"
          className="btn btn-link btn-sm px-0 mt-1"
          onClick={addActionGroup}
        >
          <FontAwesomeIcon icon={faPlus} /> Add action group
        </button>
      </div>

      <div className={styles.applyBar}>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => setActiveStep("condition")}
        >
          Back
        </button>
        <button
          type="button"
          className="btn btn-success btn-sm"
          onClick={handleApplyAction}
        >
          Apply actions
        </button>
      </div>
    </div>
  );

  let sidebarContent;
  if (activeStep === "trigger") sidebarContent = renderTriggerSidebar();
  else if (activeStep === "condition")
    sidebarContent = renderConditionSidebar();
  else if (activeStep === "action") sidebarContent = renderActionSidebar();

  const triggerSummary = getTriggerSummary(selectedModule, selectedTrigger);

  // summaries per group for graph
  const conditionSummariesByGroup = conditionGroups.map((group) => ({
    groupId: group.groupId,
    lines: getConditionSummaryLines(group.rows.map((r) => r.condition)),
  }));

  const actionSummariesByGroup = actionGroups.map((group) => ({
    groupId: group.groupId,
    lines: getActionSummaryLines(group.rows.map((r) => r.action)),
  }));
  const templateOptions = emailTemplate.map((item) => ({
    value: item.id,
    label: item.template_name,
  }));

  useEffect(() => {
    if (!selectedModule) return;
    if (isEditMode) return;

    setValue("trigger", null);

    if (conditions.length) {
      removeCondition(conditions.map((_, index) => index));
    }

    if (actions.length) {
      removeAction(actions.map((_, index) => index));
    }

    setConfigured({
      trigger: false,
      condition: false,
      action: false,
    });

    setConditionType(null);
    setActiveStep("trigger");
  }, [selectedModule]);

  const onSubmitError = (formErrors) => {
    if (formErrors?.conditions) {
      toast.error("Please add at least one condition");
      return;
    }

    if (formErrors?.actions) {
      toast.error("Please add at least one action");
      return;
    }

    toast.error("Please complete all required steps before saving");
  };

  return (
    <>
      {!isWorkFlow && <UpgradePlan moduleName="CreateWorkflow" />}

      {!workflowGrants?.isCreate ? (
        <AuthorizationCard />
      ) : (
        <div className={styles.workflowPage}>
          <form
            onSubmit={handleSubmit(onSubmit, onSubmitError)}
            className={styles.fullHeightForm}
          >
            {/* Header */}
            <div className={styles.workflowHeader}>
              <div>
                <h4 className={styles.headerTitle}>{isEditMode ? "Edit Workflow" :"New Workflow"}</h4>
                <div className={styles.headerSubTitle}>
                  Build automations for Leads, Contacts, Quotations, Projects &
                  Tasks.
                </div>
              </div>
              <div className="d-flex gap-2">
                <Button
                  className="btn btn-default-outline"
                  onClick={() => navigate("/workflow")}
                >
                  <BsArrowLeft className="me-1" /> Back
                </Button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm px-3"
                  onClick={handleCancel}
                >
                  Reset
                </button>
                {/* <button type="submit" className="btn btn-default">
              Save
            </button> */}
                <button
                  type="button"
                  className="btn btn-default"
                  onClick={() => {
                    if (!configured.trigger) {
                      toast.error("Please apply trigger");
                      setActiveStep("trigger");
                      return;
                    }
                    if (!configured.condition) {
                      toast.error("Please apply conditions");
                      setActiveStep("condition");
                      return;
                    }
                    if (!configured.action) {
                      toast.error("Please apply actions");
                      setActiveStep("action");
                      return;
                    }

                    handleSubmit(onSubmit, onSubmitError)();
                  }}
                >
                  Save
                </button>
              </div>
            </div>

            <div className={styles.workflowBody}>
              {/* LEFT SIDE - REACT FLOW CANVAS */}
              <div
                className={styles.builderCanvas}
                style={{ position: "relative", minHeight: 500 }}
              >
                {/* React Flow Graph */}
                <WorkflowCanvas
                  configured={configured}
                  activeStep={activeStep}
                  triggerSummary={triggerSummary}
                  conditionSummariesByGroup={conditionSummariesByGroup}
                  actionSummariesByGroup={actionSummariesByGroup}
                  setActiveStep={setActiveStep}
                  addConditionGroup={addConditionGroup}
                  removeConditionGroup={removeConditionGroup}
                  addActionGroup={addActionGroup}
                  removeActionGroup={removeActionGroup}
                  editConditionsNode={editConditionsNode}
                  editActionNode={editActionNode}
                  conditionGroups={conditionGroups}
                  actionGroups={actionGroups}
                  styles={styles}
                  workflowId={paramId}
                  isEditMode={isEditMode}
                />
              </div>

              {/* RIGHT SIDEBAR */}
              {/* --- DRAWER OVERLAY --- */}
              {activeStep && (
                <div
                  className={styles.drawerOverlay}
                  onClick={() => setActiveStep(null)}
                />
              )}

              {/* --- DRAWER PANEL --- */}
              <div
                className={`${styles.drawer} ${
                  activeStep ? styles.drawerOpen : ""
                }`}
              >
                <div className={styles.drawerHeader}>
                  {/* Tabs */}
                  <div className={styles.drawerTabs}>
                    <button
                      type="button"
                      className={`${styles.drawerTab} ${
                        activeStep === "trigger" ? styles.activeTab : ""
                      }`}
                      onClick={() => setActiveStep("trigger")}
                    >
                      Trigger
                    </button>

                    <button
                      type="button"
                      className={`${styles.drawerTab} ${
                        activeStep === "condition" ? styles.activeTab : ""
                      }`}
                      disabled={!configured.trigger}
                      onClick={() => setActiveStep("condition")}
                    >
                      Condition
                    </button>

                    <button
                      type="button"
                      className={`${styles.drawerTab} ${
                        activeStep === "action" ? styles.activeTab : ""
                      }`}
                      disabled={!configured.condition}
                      onClick={() => setActiveStep("action")}
                    >
                      Action
                    </button>
                  </div>

                  {/* Close */}
                  <button
                    type="button"
                    className={styles.drawerClose}
                    onClick={() => setActiveStep(null)}
                  >
                    ✖
                  </button>
                </div>

                {sidebarContent}
              </div>
            </div>
          </form>

          {/* ACTION CONFIG MODAL */}
          {!addteamplateX && configModal.open && (
            <div
              className={`modal d-block ${styles.modalBackdrop}`}
              tabIndex="-1"
            >
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {configModal.actionType === "sendEmail" && (
                        <>
                          Configure Email
                          <p className="text-muted fs-11 fw-400 mt-1">
                            Configure recipients and select a template to send
                            this email automatically.
                          </p>{" "}
                        </>
                      )}
                      {configModal.actionType === "sendWhatsapp" && (
                        <>
                          Configure WhatsApp{" "}
                          <p className="text-muted fs-11 fw-400 mt-1">
                            Set up recipients and choose a template to send
                            WhatsApp messages automatically.
                          </p>
                        </>
                      )}
                      {configModal.actionType === "updateField" &&
                        "Update Field"}
                      {configModal.actionType === "createTask" && "Create Task"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeConfigModal}
                    />
                  </div>

                  <div className="modal-body">
                    {/* EMAIL MODAL */}
                    {configModal.actionType === "sendEmail" && (
                      <>
                        <Row md={12}>
                          <Col md={6}>
                            <div className="mb-3">
                              <span className="d-flex flex-column">
                                <span
                                  style={{
                                    fontSize: "0.86rem",
                                    color: "#878b8e ",
                                    fontWeight: " 400",
                                  }}
                                >
                                  Name
                                  <span className="text-danger">*</span>{" "}
                                </span>

                                <span className="text-muted fs-10 fw-400 mb-1">
                                  Enter a descriptive name to identify this
                                  email step.
                                </span>
                              </span>
                              <input
                                className="form-control"
                                value={configModal.draft.name || ""}
                                onChange={(e) =>
                                  updateConfigDraft("name", e.target.value)
                                }
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            {/* To */}
                            <span className="d-flex flex-column">
                              <span
                                style={{
                                  fontSize: "0.86rem",
                                  color: "#878b8e ",
                                  fontWeight: " 400",
                                }}
                              >
                                To
                                <span className="text-danger">*</span>{" "}
                              </span>

                              <span className="text-muted fs-10 fw-400 mb-1">
                                Pick one or more recipients for this email.
                              </span>
                            </span>

                            <Select
                              isMulti
                              options={[
                                { value: "owner", label: "Owner" },
                                {
                                  value: "owner_manager",
                                  label: "Owner’s Manager",
                                },
                                { value: "created_by", label: "Created By" },
                              ]}
                              value={(configModal.draft.to || []).map((v) => ({
                                value: v,
                                label:
                                  v === "owner"
                                    ? "Owner"
                                    : v === "owner_manager"
                                      ? "Owner’s Manager"
                                      : "Created By",
                              }))}
                              onChange={(selected) => {
                                const values = selected.map(
                                  (item) => item.value,
                                );
                                updateConfigDraft("to", values);
                              }}
                              styles={customSelectStyles}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            {/* Recipients (Tags Input) */}
                            <div className="d-flex flex-column">
                              {/* <label className="form-label ">Recipients</label> */}
                              <span
                                style={{
                                  fontSize: "0.86rem",
                                  color: "#878b8e ",
                                  fontWeight: " 400",
                                }}
                              >
                                Recipients
                              </span>
                              <span className="text-muted fs-10 fw-400 mb-1">
                                Add recipients and press Enter to include
                                multiple emails.
                              </span>
                            </div>
                            <TagsInput
                              value={configModal.draft.recipients || []}
                              onChange={(tags) =>
                                updateConfigDraft("recipients", tags)
                              }
                            />
                            <style>
                              {`
                              .react-tagsinput-tag {
                                  background-color : #1770ff !important;
                                  color : white;
                                  
                              }
                              .react-tagsinput-remove{
                                  color : white;
                              }
                              .react-tagsinput-input{
                                  width : 100%;
                                  padding: 0rem 0rem 0rem !important;
                                      min-height: 30px !important;
                              }
                              .custom{
                                  padding-left : 0.5rem;
                                  margin-top : 9px;
                              }
                          `}
                            </style>
                          </Col>
                          <Col md={6}>
                            {/* Templates */}
                            <span className="d-flex flex-column">
                              <span
                                style={{
                                  fontSize: "0.86rem",
                                  color: "#878b8e ",
                                  fontWeight: " 400",
                                }}
                              >
                                Template
                                <span className="text-danger">*</span>{" "}
                              </span>

                              <span className="text-muted fs-10 fw-400 mb-1">
                                Select a predefined template for this email.
                              </span>
                            </span>

                            <Select
                              styles={customSelectStyles}
                              placeholder="Select Template"
                              options={templateOptions}
                              isClearable
                              components={{ MenuList }}
                              isEditMode={isEditMode}
                              value={
                                templateOptions.find(
                                  (opt) =>
                                    opt.value === configModal.draft.template_id,
                                ) || null
                              }
                              onChange={(selectedOption) => {
                                handleEmailTemplateSelect(
                                  selectedOption ? selectedOption.value : "",
                                );
                              }}
                              onAddNew={() => {
                                handleAddEmailTemplateClick();
                              }}
                            />
                          </Col>
                        </Row>

                        {/* Subject */}
                        <div className="mt-3">
                          <span className="d-flex flex-column">
                            <span
                              style={{
                                fontSize: "0.86rem",
                                color: "#878b8e ",
                                fontWeight: " 400",
                              }}
                            >
                              Subject
                              <span className="text-danger">*</span>{" "}
                            </span>

                            <span className="text-muted fs-10 fw-400 mb-1">
                              This subject is generated from the chosen
                              template.
                            </span>
                          </span>
                          <input
                            readOnly
                            className="form-control"
                            value={configModal.draft.subject || ""}
                            onChange={(e) =>
                              updateConfigDraft("subject", e.target.value)
                            }
                          />
                        </div>

                        {/* Body */}
                        <div className="mt-3">
                          <span className="d-flex flex-column">
                            <span
                              style={{
                                fontSize: "0.86rem",
                                color: "#878b8e ",
                                fontWeight: " 400",
                              }}
                            >
                              Body
                              <span className="text-danger">*</span>{" "}
                            </span>

                            <span className="text-muted fs-10 fw-400 mb-1">
                              Email content will be populated from the selected
                              template.
                            </span>
                          </span>

                          <div
                            className="border border-1"
                            style={{
                              minHeight: "100px",
                              maxHeight: "150px",
                              overflow: "scroll",
                              padding: "1px 11px",
                            }}
                            dangerouslySetInnerHTML={{
                              __html: configModal?.draft?.body,
                            }}
                          />
                        </div>
                      </>
                    )}

                    {/* WHATSAPP MODAL */}

                    {configModal.actionType === "sendWhatsapp" && (
                      <>
                        <Row md={12}>
                          <Col md={6}>
                            {/* Name */}
                            <div className="mb-3">
                              <span className="d-flex flex-column">
                                <span
                                  style={{
                                    fontSize: "0.86rem",
                                    color: "#878b8e ",
                                    fontWeight: " 400",
                                  }}
                                >
                                  Name
                                  <span className="text-danger">*</span>{" "}
                                </span>

                                <span className="text-muted fs-10 fw-400 mb-1">
                                  Enter a name for this workflow.
                                </span>
                              </span>
                              <input
                                className="form-control"
                                placeholder="Enter a name for this workflow"
                                value={configModal.draft.name || ""}
                                onChange={(e) =>
                                  updateConfigDraft("name", e.target.value)
                                }
                              />
                            </div>
                          </Col>
                          <Col md={6}>
                            {/* Template ID + Fetch */}
                            <div className="mb-3">
                              <span className="d-flex flex-column">
                                <span
                                  style={{
                                    fontSize: "0.86rem",
                                    color: "#878b8e ",
                                    fontWeight: " 400",
                                  }}
                                >
                                  Template Name
                                </span>

                                <span className="text-muted fs-10 fw-400 mb-1">
                                  Enter the approved template name.
                                </span>
                              </span>
                              <div
                                className="d-flex gap-2"
                                style={{ width: "18.43rem" }}
                              >
                                {/* <Select
                              options={whatsappTemplateDataOption}
                              placeholder="Select Template"
                              isClearable
                              styles={customSelectStyles}
                              components={{ MenuList }}
                              isEditMode={isEditMode}
                              value={selectedTemplate}
                              onChange={(option) => {
                                setSelectedTemplate(option);

                                if (!option) {
                                  updateConfigDraft("templateId", "");
                                  updateConfigDraft("templateName", "");
                                  updateConfigDraft("templateContent", "");
                                  updateConfigDraft("templateVariables", {});
                                  updateConfigDraft("variableValues", {});
                                  setDynamicFields([]);
                                  return;
                                }

                                updateConfigDraft("templateId", option.value);
                                updateConfigDraft("templateName", option.label);

                                updateConfigDraft("templateContent", "");
                                updateConfigDraft("templateVariables", {});
                                updateConfigDraft("variableValues", {});
                                setDynamicFields([]);
                              }}
                              onAddNew={() => {
                                handleAddTemplateClick();
                              }}
                            /> */}
                                <Select
                                  options={whatsappTemplateDataOption}
                                  placeholder="Select Template"
                                  isClearable
                                  styles={{
                                    ...customSelectStyles,
                                    container: (base, state) => ({
                                      ...(customSelectStyles.container
                                        ? customSelectStyles.container(
                                            base,
                                            state,
                                          )
                                        : base),
                                      minWidth: "18.43rem",
                                    }),
                                    control: (base, state) => ({
                                      ...(customSelectStyles.control
                                        ? customSelectStyles.control(
                                            base,
                                            state,
                                          )
                                        : base),
                                      minWidth: "18.43rem",
                                    }),
                                  }}
                                  components={{ MenuList }}
                                  isEditMode={isEditMode}
                                  value={selectedTemplate}
                                  onChange={(option) => {
                                    setSelectedTemplate(option);

                                    if (!option) {
                                      updateConfigDraft("templateId", "");
                                      updateConfigDraft("templateName", "");
                                      updateConfigDraft("templateContent", "");
                                      updateConfigDraft(
                                        "templateVariables",
                                        {},
                                      );
                                      updateConfigDraft("variableValues", {});
                                      setDynamicFields([]);
                                      return;
                                    }

                                    updateConfigDraft(
                                      "templateId",
                                      option.value,
                                    );
                                    updateConfigDraft(
                                      "templateName",
                                      option.label,
                                    );

                                    updateConfigDraft("templateContent", "");
                                    updateConfigDraft("templateVariables", {});
                                    updateConfigDraft("variableValues", {});
                                    setDynamicFields([]);
                                  }}
                                  onAddNew={handleAddTemplateClick}
                                />

                                <button
                                  className="btn btn-primary"
                                  type="button"
                                  onClick={onWhatsAPPFetch}
                                >
                                  Fetch
                                </button>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        {isLoading ? (
                          <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* To */}
                            <Row md={12}>
                              <Col md={6}>
                                <div className="mb-3">
                                  <span className="d-flex flex-column">
                                    <span
                                      style={{
                                        fontSize: "0.86rem",
                                        color: "#878b8e ",
                                        fontWeight: " 400",
                                      }}
                                    >
                                      To
                                      <span className="text-danger">
                                        *
                                      </span>{" "}
                                    </span>

                                    <span className="text-muted fs-10 fw-400 mb-1">
                                      Select a recipient (Owner, Created By, or
                                      Manager).
                                    </span>
                                  </span>
                                  <Select
                                    isMulti
                                    options={[
                                      { value: "owner", label: "Owner" },
                                      {
                                        value: "owner_manager",
                                        label: "Owner’s Manager",
                                      },
                                      {
                                        value: "created_by",
                                        label: "Created By",
                                      },
                                    ]}
                                    value={(configModal.draft.to || []).map(
                                      (v) => ({
                                        value: v,
                                        label:
                                          v === "owner"
                                            ? "Owner"
                                            : v === "owner_manager"
                                              ? "Owner’s Manager"
                                              : "Created By",
                                      }),
                                    )}
                                    onChange={(selected) => {
                                      const values = selected.map(
                                        (item) => item.value,
                                      );
                                      updateConfigDraft("to", values);
                                    }}
                                    styles={customSelectStyles}
                                    placeholder="Select recipients"
                                  />
                                </div>
                              </Col>
                              <Col md={6}>
                                {/* Recipients (Tags Input) */}
                                <div className="d-flex flex-column">
                                  {/* <label className="form-label ">Recipients</label> */}
                                  <span
                                    style={{
                                      fontSize: "0.86rem",
                                      color: "#878b8e ",
                                      fontWeight: " 400",
                                    }}
                                  >
                                    Recipients
                                  </span>
                                  <span className="text-muted fs-10 fw-400 mb-1">
                                    Add your country code (e.g., +91) before the
                                    phone number.
                                  </span>
                                </div>
                                <TagsInput
                                  value={configModal.draft.recipients || []}
                                  onChange={(tags) =>
                                    updateConfigDraft("recipients", tags)
                                  }
                                />

                                <style>
                                  {`
                              .react-tagsinput-tag {
                                  background-color : #1770ff !important;
                                  color : white;
                                  
                              }
                              .react-tagsinput-remove{
                                  color : white;
                              }
                              .react-tagsinput-input{
                                  width : 100%;
                                  padding: 0rem 0rem 0rem !important;
                                      min-height: 30px !important;
                              }


                              .custom{
                                  padding-left : 0.5rem;
                                  margin-top : 9px;
                              }
                          `}
                                </style>
                              </Col>
                            </Row>

                            {/* Template Content (readonly) */}
                            <div className="mb-3">
                              <span className="d-flex flex-column">
                                <span
                                  style={{
                                    fontSize: "0.86rem",
                                    color: "#878b8e ",
                                    fontWeight: " 400",
                                  }}
                                >
                                  Template Content
                                  <span className="text-danger">*</span>{" "}
                                </span>

                                <span className="text-muted fs-10 fw-400 mb-1">
                                  Template message will appear here.
                                </span>
                              </span>
                              <textarea
                                className="form-control"
                                rows={3}
                                value={configModal.draft.templateContent || ""}
                                readOnly
                              />
                            </div>

                            {/* Dynamic Variables */}
                            {dynamicFields.length > 0 && (
                              <div className="mt-4">
                                <span className="d-flex flex-column">
                                  <span
                                    style={{
                                      fontSize: "0.86rem",
                                      color: "#878b8e ",
                                      fontWeight: " 400",
                                    }}
                                  >
                                    Template Variables
                                    <span className="text-danger">*</span>{" "}
                                  </span>

                                  <span className="text-muted fs-10 fw-400 mb-3">
                                    Provide values for each template variable.
                                  </span>
                                </span>

                                {renderDynamicFields()}
                              </div>
                            )}
                          </>
                        )}
                      </>
                    )}

                    {/* SMS MODAL */}
                    {configModal.actionType === "sendSMS" && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Name</label>
                          <input
                            className="form-control"
                            value={configModal.draft.name || ""}
                            onChange={(e) =>
                              updateConfigDraft("name", e.target.value)
                            }
                          />
                        </div>

                        {/* To */}
                        <label className="form-label">To</label>
                        <select
                          className="form-select"
                          multiple
                          value={configModal.draft.to || []}
                          onChange={(e) => {
                            const selectedValues = Array.from(
                              e.target.selectedOptions,
                            ).map((option) => option.value);
                            updateConfigDraft("to", selectedValues);
                          }}
                        >
                          <option value="owner">Owner</option>
                          <option value="owner_manager">Owner’s Manager</option>
                          <option value="created_by">Created By</option>
                        </select>

                        {/* Recipients */}
                        <label className="form-label mt-3">Recipients</label>
                        <TagsInput
                          value={configModal.draft.recipients || []}
                          onChange={(tags) =>
                            updateConfigDraft("recipients", tags)
                          }
                        />

                        {/* Body */}
                        <label className="form-label mt-3">Message</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={configModal.draft.body || ""}
                          onChange={(e) =>
                            updateConfigDraft("body", e.target.value)
                          }
                        />
                      </>
                    )}

                    {configModal.actionType === "createTask" && (
                      <>
                        <div className="mb-3">
                          <label className="form-label">Task Name</label>
                          <input
                            className="form-control"
                            value={configModal.draft.taskName || ""}
                            onChange={(e) =>
                              updateConfigDraft("taskName", e.target.value)
                            }
                          />
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Due Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={configModal.draft.dueDate || ""}
                            onChange={(e) =>
                              updateConfigDraft("dueDate", e.target.value)
                            }
                          />
                        </div>
                        <div className="mb-0">
                          <label className="form-label">Notes</label>
                          <textarea
                            className="form-control"
                            rows={3}
                            value={configModal.draft.notes || ""}
                            onChange={(e) =>
                              updateConfigDraft("notes", e.target.value)
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-default-outline "
                      onClick={closeConfigModal}
                    >
                      Close
                    </button>
                    {configModal.actionType === "sendWhatsapp" ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-default"
                          onClick={saveConfigModal}
                          disabled={dynamicFields?.length <= 0}
                        >
                          Save
                        </button>{" "}
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={saveConfigModal}
                        >
                          Save
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          {show && (
            <NotificationTemplates
              show={show}
              setShow={setShow}
              moduleType={moduleType}
              setModuleType={setModuleType}
              setAddTemplate={setAddTemplate}
              setSelectedTemplate={setSelectedTemplate}
              setWhatsappTemplateDataOption={setWhatsappTemplateDataOption}
            />
          )}
        </div>
      )}
    </>
  );
};

export default WorkFlow;